import React, { useState, useEffect, useMemo } from 'react';
import axios from '../api/axios';
import { toast } from '../components/Toast';
import SearchableSelect from '../components/SearchableSelect';
import { formatCurrency } from '../utils/currency';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';
import './ReportsPage.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

function ReportsPage() {
  const [reportType, setReportType] = useState('PROJECT');
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Filter states
  const [enquiryFilters, setEnquiryFilters] = useState({
    startDate: '',
    endDate: '',
    status: ''
  });
  
  const [projectFilters, setProjectFilters] = useState({
    status: '',
    customerId: '',
    projectId: '',
    startDate: '',
    endDate: ''
  });
  
  const [expenseFilters, setExpenseFilters] = useState({
    projectId: '',
    category: '',
    vendorId: '',
    startDate: '',
    endDate: ''
  });
  
  const [payrollFilters, setPayrollFilters] = useState({
    employeeId: '',
    payPeriodStart: '',
    payPeriodEnd: ''
  });

  const [dailyFilters, setDailyFilters] = useState({
    date: new Date().toISOString().split('T')[0],
    projectId: ''
  });
  const [activeDailyTab, setActiveDailyTab] = useState('supervisor'); // 'supervisor' | 'timesheets' | 'transactions' | 'attendance' | 'sitestock'
  const [dailySupSearch, setDailySupSearch] = useState('');
  const [dailyTimeSearch, setDailyTimeSearch] = useState('');
  const [dailyTxnSearch, setDailyTxnSearch] = useState('');
  const [dailyTxnType, setDailyTxnType] = useState('ALL');
  const [dailyAttSearch, setDailyAttSearch] = useState('');
  const [dailyAttStatus, setDailyAttStatus] = useState('ALL');
  const [dailyStockSearch, setDailyStockSearch] = useState('');
  const [dailyStockSubTab, setDailyStockSubTab] = useState('stocks'); // 'stocks' | 'movements'
  
  // Project Report Extra Details & Analytics States
  const [activeProjectTab, setActiveProjectTab] = useState('analytics'); // 'analytics' | 'projects' | 'transactions' | 'wbs' | 'supervisor' | 'expenses' | 'materials'
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [selectedProjectName, setSelectedProjectName] = useState('');
  const [projectTransactions, setProjectTransactions] = useState([]);
  const [projectExpenses, setProjectExpenses] = useState([]);
  const [projectWbsItems, setProjectWbsItems] = useState([]);
  const [projectSupervisorUpdates, setProjectSupervisorUpdates] = useState([]);
  const [projectMaterialStocks, setProjectMaterialStocks] = useState([]);
  const [projectMaterialHistory, setProjectMaterialHistory] = useState([]);
  const [projectDetailsLoading, setProjectDetailsLoading] = useState(false);
  const [txnTypeFilter, setTxnTypeFilter] = useState('ALL');
  const [txnSearchTerm, setTxnSearchTerm] = useState('');
  const [wbsStatusFilter, setWbsStatusFilter] = useState('ALL');
  const [wbsSearchTerm, setWbsSearchTerm] = useState('');
  const [supSearchTerm, setSupSearchTerm] = useState('');
  const [matSearchTerm, setMatSearchTerm] = useState('');
  const [matTypeFilter, setMatTypeFilter] = useState('ALL');
  const [matSubTab, setMatSubTab] = useState('stocks'); // 'stocks' | 'history'
  
  // Dropdown data
  const [customers, setCustomers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [employees, setEmployees] = useState([]);
  
  const enquiryStatuses = ['NEW', 'FOLLOWUP', 'CONVERTED', 'LOST'];
  const projectStatuses = ['PLANNED', 'ACTIVE', 'DELAYED', 'COMPLETED', 'CANCELLED'];
  const expenseCategories = ['MATERIAL', 'LABOR', 'EQUIPMENT', 'SUBCONTRACTOR', 'TRANSPORT', 'OTHER'];
  
  useEffect(() => {
    fetchDropdownData();
  }, []);
  
  const fetchDropdownData = async () => {
    // Fetch each independently — silently ignore 403s for restricted roles
    const safeGet = async (url) => {
      try {
        const res = await axios.get(url);
        return res.data.data?.content || res.data.content || res.data.data || [];
      } catch {
        return [];
      }
    };

    const [customersData, projectsData, vendorsData, employeesData] = await Promise.all([
      safeGet('/customers?size=1000'),
      safeGet('/projects?size=1000'),
      safeGet('/vendors?size=1000'),
      safeGet('/employees?size=1000'),
    ]);

    setCustomers(customersData);
    setProjects(projectsData);
    setVendors(vendorsData);
    setEmployees(employeesData);
  };
  
  const fetchProjectDetails = async (projId, projName) => {
    if (!projId) return;
    setProjectDetailsLoading(true);
    setSelectedProjectId(projId);
    setSelectedProjectName(projName || `Project #${projId}`);

    try {
      const [txnsRes, expRes, wbsRes, supRes] = await Promise.all([
        axios.get(`/financial-transactions/project/${projId}`).catch(() => ({ data: { data: [] } })),
        axios.get(`/projects/${projId}/expenses`).catch(() => ({ data: { data: [] } })),
        axios.get(`/projects/${projId}/wbs`).catch(() => ({ data: { data: [] } })),
        axios.get(`/projects/${projId}/supervisor-updates`).catch(() => ({ data: { data: [] } }))
      ]);

      const txns = txnsRes.data?.data || txnsRes.data || [];
      const exps = expRes.data?.data || expRes.data || [];
      const wbs = wbsRes.data?.data || wbsRes.data || [];
      const sups = supRes.data?.data || supRes.data || [];
      setProjectTransactions(Array.isArray(txns) ? txns : []);
      setProjectExpenses(Array.isArray(exps) ? exps : []);
      setProjectWbsItems(Array.isArray(wbs) ? wbs : []);
      setProjectSupervisorUpdates(Array.isArray(sups) ? sups : []);
      if (reportData?.materialStocks) {
        setProjectMaterialStocks(reportData.materialStocks.filter(m => String(m.projectId) === String(projId)));
      }
      if (reportData?.materialHistory) {
        setProjectMaterialHistory(reportData.materialHistory.filter(m => String(m.projectId) === String(projId)));
      }
    } catch (err) {
      console.error('Failed to load project details:', err);
    } finally {
      setProjectDetailsLoading(false);
    }
  };
  
  const handleGenerateReport = async () => {
    setLoading(true);
    setReportData(null);
    
    try {
      let endpoint = '';
      let params = new URLSearchParams();
      
      switch (reportType) {
        case 'ENQUIRY':
          endpoint = '/reports/enquiries';
          if (enquiryFilters.startDate) params.append('startDate', enquiryFilters.startDate);
          if (enquiryFilters.endDate) params.append('endDate', enquiryFilters.endDate);
          if (enquiryFilters.status) params.append('status', enquiryFilters.status);
          break;
          
        case 'PROJECT':
          endpoint = '/reports/projects';
          if (projectFilters.status) params.append('status', projectFilters.status);
          if (projectFilters.customerId) params.append('customerId', projectFilters.customerId);
          const genProjId = projectFilters.projectId || selectedProjectId;
          if (genProjId) params.append('projectId', genProjId);
          if (projectFilters.startDate) params.append('startDate', projectFilters.startDate);
          if (projectFilters.endDate) params.append('endDate', projectFilters.endDate);
          break;
          
        case 'EXPENSE':
          endpoint = '/reports/expenses';
          if (expenseFilters.projectId) params.append('projectId', expenseFilters.projectId);
          if (expenseFilters.category) params.append('category', expenseFilters.category);
          if (expenseFilters.vendorId) params.append('vendorId', expenseFilters.vendorId);
          if (expenseFilters.startDate) params.append('startDate', expenseFilters.startDate);
          if (expenseFilters.endDate) params.append('endDate', expenseFilters.endDate);
          break;
          
        case 'PAYROLL':
          endpoint = '/reports/payroll';
          if (payrollFilters.employeeId) params.append('employeeId', payrollFilters.employeeId);
          if (payrollFilters.payPeriodStart) params.append('payPeriodStart', payrollFilters.payPeriodStart);
          if (payrollFilters.payPeriodEnd) params.append('payPeriodEnd', payrollFilters.payPeriodEnd);
          break;

        case 'DAILY':
          endpoint = '/reports/daily';
          if (dailyFilters.date) params.append('date', dailyFilters.date);
          if (dailyFilters.projectId) params.append('projectId', dailyFilters.projectId);
          break;
          
        case 'PROFITABILITY':
          endpoint = '/reports/profitability';
          break;
          
        default:
          throw new Error('Invalid report type');
      }
      
      const response = await axios.get(`${endpoint}?${params.toString()}`);
      let data = response.data.data || response.data;
      if (reportType === 'PROJECT' && projectFilters.projectId && data && Array.isArray(data.rows)) {
        data = {
          ...data,
          rows: data.rows.filter(r => String(r['ID']) === String(projectFilters.projectId))
        };
      }
      setReportData(data);
      if (reportType === 'PROJECT') {
        if (data.transactions && Array.isArray(data.transactions) && data.transactions.length > 0) {
          setProjectTransactions(data.transactions);
        }
        if (data.workBreakdown && Array.isArray(data.workBreakdown) && data.workBreakdown.length > 0) {
          setProjectWbsItems(data.workBreakdown);
        }
        if (data.supervisorUpdates && Array.isArray(data.supervisorUpdates) && data.supervisorUpdates.length > 0) {
          setProjectSupervisorUpdates(data.supervisorUpdates);
        }
        if (data.materialStocks && Array.isArray(data.materialStocks)) {
          setProjectMaterialStocks(data.materialStocks);
        }
        if (data.materialHistory && Array.isArray(data.materialHistory)) {
          setProjectMaterialHistory(data.materialHistory);
        }
        const targetProjId = projectFilters.projectId || (data.rows && data.rows.length === 1 ? data.rows[0]['ID'] : null);
        const targetProjName = targetProjId
          ? (projects.find(p => String(p.id) === String(targetProjId))?.name || (data.rows && data.rows[0] ? data.rows[0]['Name'] : ''))
          : '';
        if (targetProjId) {
          fetchProjectDetails(targetProjId, targetProjName);
        } else if (data.rows && data.rows.length > 0) {
          // Default to first project for initial details inspection
          fetchProjectDetails(data.rows[0]['ID'], data.rows[0]['Name']);
        } else {
          setSelectedProjectId(null);
          setSelectedProjectName('');
          setProjectTransactions(data.transactions || []);
          setProjectExpenses([]);
          setProjectWbsItems(data.workBreakdown || []);
          setProjectSupervisorUpdates(data.supervisorUpdates || []);
          setProjectMaterialStocks(data.materialStocks || []);
          setProjectMaterialHistory(data.materialHistory || []);
        }
      }
      toast.success('Report generated successfully');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to generate report');
    } finally {
      setLoading(false);
    }
  };
  
  const handleExportPdf = async () => {
    setLoading(true);
    
    try {
      let endpoint = '';
      let params = new URLSearchParams();
      
      switch (reportType) {
        case 'ENQUIRY':
          endpoint = '/reports/enquiries/export/pdf';
          if (enquiryFilters.startDate) params.append('startDate', enquiryFilters.startDate);
          if (enquiryFilters.endDate) params.append('endDate', enquiryFilters.endDate);
          if (enquiryFilters.status) params.append('status', enquiryFilters.status);
          break;
          
        case 'PROJECT':
          endpoint = '/reports/projects/export/pdf';
          if (projectFilters.status) params.append('status', projectFilters.status);
          if (projectFilters.customerId) params.append('customerId', projectFilters.customerId);
          const pdfProjId = projectFilters.projectId || selectedProjectId;
          if (pdfProjId) params.append('projectId', pdfProjId);
          if (projectFilters.startDate) params.append('startDate', projectFilters.startDate);
          if (projectFilters.endDate) params.append('endDate', projectFilters.endDate);
          break;
          
        case 'EXPENSE':
          endpoint = '/reports/expenses/export/pdf';
          if (expenseFilters.projectId) params.append('projectId', expenseFilters.projectId);
          if (expenseFilters.category) params.append('category', expenseFilters.category);
          if (expenseFilters.vendorId) params.append('vendorId', expenseFilters.vendorId);
          if (expenseFilters.startDate) params.append('startDate', expenseFilters.startDate);
          if (expenseFilters.endDate) params.append('endDate', expenseFilters.endDate);
          break;
          
        case 'PAYROLL':
          endpoint = '/reports/payroll/export/pdf';
          if (payrollFilters.employeeId) params.append('employeeId', payrollFilters.employeeId);
          if (payrollFilters.payPeriodStart) params.append('payPeriodStart', payrollFilters.payPeriodStart);
          if (payrollFilters.payPeriodEnd) params.append('payPeriodEnd', payrollFilters.payPeriodEnd);
          break;

        case 'DAILY':
          endpoint = '/reports/daily/export/pdf';
          if (dailyFilters.date) params.append('date', dailyFilters.date);
          if (dailyFilters.projectId) params.append('projectId', dailyFilters.projectId);
          break;
          
        case 'PROFITABILITY':
          endpoint = '/reports/profitability/export/pdf';
          break;
          
        default:
          throw new Error('Invalid report type');
      }
      
      const response = await axios.get(`${endpoint}?${params.toString()}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${reportType.toLowerCase()}_report_${new Date().getTime()}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Report exported to PDF successfully');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to export report to PDF');
    } finally {
      setLoading(false);
    }
  };
  
  const handleExportExcel = async () => {
    setLoading(true);
    
    try {
      let endpoint = '';
      let params = new URLSearchParams();
      
      switch (reportType) {
        case 'ENQUIRY':
          endpoint = '/reports/enquiries/export/excel';
          if (enquiryFilters.startDate) params.append('startDate', enquiryFilters.startDate);
          if (enquiryFilters.endDate) params.append('endDate', enquiryFilters.endDate);
          if (enquiryFilters.status) params.append('status', enquiryFilters.status);
          break;
          
        case 'PROJECT':
          endpoint = '/reports/projects/export/excel';
          if (projectFilters.status) params.append('status', projectFilters.status);
          if (projectFilters.customerId) params.append('customerId', projectFilters.customerId);
          const excelProjId = projectFilters.projectId || selectedProjectId;
          if (excelProjId) params.append('projectId', excelProjId);
          if (projectFilters.startDate) params.append('startDate', projectFilters.startDate);
          if (projectFilters.endDate) params.append('endDate', projectFilters.endDate);
          break;
          
        case 'EXPENSE':
          endpoint = '/reports/expenses/export/excel';
          if (expenseFilters.projectId) params.append('projectId', expenseFilters.projectId);
          if (expenseFilters.category) params.append('category', expenseFilters.category);
          if (expenseFilters.vendorId) params.append('vendorId', expenseFilters.vendorId);
          if (expenseFilters.startDate) params.append('startDate', expenseFilters.startDate);
          if (expenseFilters.endDate) params.append('endDate', expenseFilters.endDate);
          break;
          
        case 'PAYROLL':
          endpoint = '/reports/payroll/export/excel';
          if (payrollFilters.employeeId) params.append('employeeId', payrollFilters.employeeId);
          if (payrollFilters.payPeriodStart) params.append('payPeriodStart', payrollFilters.payPeriodStart);
          if (payrollFilters.payPeriodEnd) params.append('payPeriodEnd', payrollFilters.payPeriodEnd);
          break;

        case 'DAILY':
          endpoint = '/reports/daily/export/excel';
          if (dailyFilters.date) params.append('date', dailyFilters.date);
          if (dailyFilters.projectId) params.append('projectId', dailyFilters.projectId);
          break;
          
        case 'PROFITABILITY':
          endpoint = '/reports/profitability/export/excel';
          break;
          
        default:
          throw new Error('Invalid report type');
      }
      
      const response = await axios.get(`${endpoint}?${params.toString()}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${reportType.toLowerCase()}_report_${new Date().getTime()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Report exported to Excel successfully');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to export report to Excel');
    } finally {
      setLoading(false);
    }
  };
  
  const renderFilters = () => {
    switch (reportType) {
      case 'ENQUIRY':
        return (
          <div className="filters-grid">
            <div className="form-group form-group-date">
              <label>Start Date</label>
              <input
                type="date"
                value={enquiryFilters.startDate}
                onChange={(e) => setEnquiryFilters({ ...enquiryFilters, startDate: e.target.value })}
              />
            </div>
            <div className="form-group form-group-date">
              <label>End Date</label>
              <input
                type="date"
                value={enquiryFilters.endDate}
                onChange={(e) => setEnquiryFilters({ ...enquiryFilters, endDate: e.target.value })}
              />
            </div>
            <div className="form-group form-group-status">
              <label>Status</label>
              <select
                value={enquiryFilters.status}
                onChange={(e) => setEnquiryFilters({ ...enquiryFilters, status: e.target.value })}
              >
                <option value="">All Statuses</option>
                {enquiryStatuses.map(status => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
            </div>
          </div>
        );
        
      case 'PROJECT': {
        const filteredProjectsForCustomer = projectFilters.customerId
          ? projects.filter(p => String(p.customerId) === String(projectFilters.customerId))
          : projects;

        return (
          <div className="filters-grid">
            <div className="form-group form-group-select-search">
              <label>Customer</label>
              <SearchableSelect
                options={customers}
                value={projectFilters.customerId}
                onChange={(val) => {
                  setProjectFilters(prev => {
                    const currentProj = projects.find(p => String(p.id) === String(prev.projectId));
                    const currentProjCustId = currentProj?.customerId || currentProj?.customer?.id;
                    const shouldClearProject = val && currentProj && String(currentProjCustId) !== String(val);
                    if (shouldClearProject) {
                      setSelectedProjectId(null);
                      setSelectedProjectName('');
                    }
                    return {
                      ...prev,
                      customerId: val,
                      projectId: shouldClearProject ? '' : prev.projectId
                    };
                  });
                }}
                placeholder="Search & select customer..."
                searchPlaceholder="Search customer by name, phone..."
                allOptionLabel="All Customers"
                getOptionLabel={(customer) => customer.name}
                getOptionValue={(customer) => customer.id}
                getOptionSubtext={(customer) => customer.phone ? `📞 ${customer.phone}` : customer.email ? `✉ ${customer.email}` : ''}
              />
            </div>
            <div className="form-group form-group-select-search">
              <label>Project</label>
              <SearchableSelect
                options={filteredProjectsForCustomer}
                value={projectFilters.projectId}
                onChange={(val, opt) => {
                  setProjectFilters(prev => {
                    const next = { ...prev, projectId: val };
                    const custId = opt?.customerId || opt?.customer?.id;
                    if (val && custId && !prev.customerId) {
                      next.customerId = String(custId);
                    }
                    return next;
                  });
                  if (val) {
                    setSelectedProjectId(val);
                    setSelectedProjectName(opt?.name || `Project #${val}`);
                  } else {
                    setSelectedProjectId(null);
                    setSelectedProjectName('');
                  }
                }}
                placeholder={projectFilters.customerId ? "Search & select customer project..." : "Search & select project..."}
                searchPlaceholder="Search project by name..."
                allOptionLabel="All Projects"
                getOptionLabel={(project) => project.name}
                getOptionValue={(project) => project.id}
                getOptionSubtext={(project) => {
                  const parts = [];
                  if (!projectFilters.customerId && (project.customerName || project.customer?.name)) {
                    parts.push(`Client: ${project.customerName || project.customer?.name}`);
                  }
                  if (project.status) parts.push(project.status);
                  return parts.join(' • ');
                }}
              />
            </div>
            <div className="form-group form-group-status">
              <label>Status</label>
              <select
                value={projectFilters.status}
                onChange={(e) => setProjectFilters({ ...projectFilters, status: e.target.value })}
              >
                <option value="">All Statuses</option>
                {projectStatuses.map(status => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
            </div>
            <div className="form-group form-group-date">
              <label>Start Date</label>
              <input
                type="date"
                value={projectFilters.startDate}
                onChange={(e) => setProjectFilters({ ...projectFilters, startDate: e.target.value })}
              />
            </div>
            <div className="form-group form-group-date">
              <label>End Date</label>
              <input
                type="date"
                value={projectFilters.endDate}
                onChange={(e) => setProjectFilters({ ...projectFilters, endDate: e.target.value })}
              />
            </div>
          </div>
        );
      }
        
      case 'EXPENSE':
        return (
          <div className="filters-grid">
            <div className="form-group form-group-select-search">
              <label>Project</label>
              <SearchableSelect
                options={projects}
                value={expenseFilters.projectId}
                onChange={(val) => setExpenseFilters({ ...expenseFilters, projectId: val })}
                placeholder="Search & select project..."
                searchPlaceholder="Search project by name, client..."
                allOptionLabel="All Projects"
                getOptionLabel={(project) => project.name}
                getOptionValue={(project) => project.id}
                getOptionSubtext={(project) => {
                  const parts = [];
                  if (project.customerName || project.customer?.name) parts.push(`Client: ${project.customerName || project.customer?.name}`);
                  if (project.status) parts.push(project.status);
                  return parts.join(' • ');
                }}
              />
            </div>
            <div className="form-group form-group-status">
              <label>Category</label>
              <select
                value={expenseFilters.category}
                onChange={(e) => setExpenseFilters({ ...expenseFilters, category: e.target.value })}
              >
                <option value="">All Categories</option>
                {expenseCategories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>
            <div className="form-group form-group-select-search">
              <label>Vendor</label>
              <SearchableSelect
                options={vendors}
                value={expenseFilters.vendorId}
                onChange={(val) => setExpenseFilters({ ...expenseFilters, vendorId: val })}
                placeholder="Search & select vendor..."
                searchPlaceholder="Search vendor by name, phone..."
                allOptionLabel="All Vendors"
                getOptionLabel={(vendor) => vendor.name}
                getOptionValue={(vendor) => vendor.id}
                getOptionSubtext={(vendor) => vendor.phone ? `📞 ${vendor.phone}` : vendor.vendorType || ''}
              />
            </div>
            <div className="form-group form-group-date">
              <label>Start Date</label>
              <input
                type="date"
                value={expenseFilters.startDate}
                onChange={(e) => setExpenseFilters({ ...expenseFilters, startDate: e.target.value })}
              />
            </div>
            <div className="form-group form-group-date">
              <label>End Date</label>
              <input
                type="date"
                value={expenseFilters.endDate}
                onChange={(e) => setExpenseFilters({ ...expenseFilters, endDate: e.target.value })}
              />
            </div>
          </div>
        );
        
      case 'PAYROLL':
        return (
          <div className="filters-grid">
            <div className="form-group form-group-select-search">
              <label>Employee</label>
              <SearchableSelect
                options={employees}
                value={payrollFilters.employeeId}
                onChange={(val) => setPayrollFilters({ ...payrollFilters, employeeId: val })}
                placeholder="Search & select employee..."
                searchPlaceholder="Search employee by name, code..."
                allOptionLabel="All Employees"
                getOptionLabel={(employee) => employee.user?.name || employee.name || employee.userName || employee.employeeCode || `Employee #${employee.id}`}
                getOptionValue={(employee) => employee.id}
                getOptionSubtext={(employee) => employee.employeeCode ? `Code: ${employee.employeeCode}` : ''}
              />
            </div>
            <div className="form-group form-group-date">
              <label>Pay Period Start</label>
              <input
                type="date"
                value={payrollFilters.payPeriodStart}
                onChange={(e) => setPayrollFilters({ ...payrollFilters, payPeriodStart: e.target.value })}
              />
            </div>
            <div className="form-group form-group-date">
              <label>Pay Period End</label>
              <input
                type="date"
                value={payrollFilters.payPeriodEnd}
                onChange={(e) => setPayrollFilters({ ...payrollFilters, payPeriodEnd: e.target.value })}
              />
            </div>
          </div>
        );

      case 'DAILY':
        return (
          <div className="filters-grid">
            <div className="form-group form-group-date">
              <label>Report Date</label>
              <input
                type="date"
                value={dailyFilters.date}
                onChange={(e) => setDailyFilters({ ...dailyFilters, date: e.target.value })}
              />
            </div>
            <div className="form-group form-group-select-search">
              <label>Project Scope (Optional)</label>
              <SearchableSelect
                options={projects}
                value={dailyFilters.projectId}
                onChange={(val) => setDailyFilters({ ...dailyFilters, projectId: val })}
                placeholder="All Projects (or search project...)"
                searchPlaceholder="Search project by name..."
                allOptionLabel="All Projects"
                getOptionLabel={(project) => project.name}
                getOptionValue={(project) => project.id}
                getOptionSubtext={(project) => {
                  const parts = [];
                  if (project.customerName || project.customer?.name) parts.push(`Client: ${project.customerName || project.customer?.name}`);
                  if (project.status) parts.push(project.status);
                  return parts.join(' • ');
                }}
              />
            </div>
          </div>
        );
        
      case 'PROFITABILITY':
        return (
          <div className="filters-info-note">
            <span className="info-icon">💡</span>
            <span>Profitability report compiles all projects with calculated contract revenue, realized expenses, and net profit margins. No additional filters needed.</span>
          </div>
        );
        
      default:
        return null;
    }
  };
  
  const formatValue = (value, column) => {
    if (value === null || value === undefined || value === '') return '-';
    const colLower = String(column || '').toLowerCase();
    const num = Number(value);
    if (!isNaN(num) && colLower.includes('margin')) {
      return `${num >= 0 ? '+' : ''}${num.toFixed(1)}%`;
    }
    if (!isNaN(num) && (
      colLower.includes('budget') ||
      colLower.includes('income') ||
      colLower.includes('expense') ||
      colLower.includes('balance') ||
      colLower.includes('amount') ||
      (colLower.includes('profit') && !colLower.includes('margin'))
    )) {
      return formatCurrency(num);
    }
    if (typeof value === 'number') {
      return value.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    return value.toString();
  };

  // Aggregated metrics for Project Report
  const projectMetrics = useMemo(() => {
    if (reportType !== 'PROJECT' || !reportData || !Array.isArray(reportData.rows)) {
      return { totalProjects: 0, totalBudget: 0, totalIncome: 0, totalExpenses: 0, netBalance: 0, profitMargin: 0, expenseUsagePercent: 0 };
    }

    const rows = reportData.rows;
    let totalBudget = 0;
    let totalIncome = 0;
    let totalExpenses = 0;

    rows.forEach(r => {
      totalBudget += Number(r['Budget'] || 0);
      totalIncome += Number(r['Total Income'] || 0);
      totalExpenses += Number(r['Total Expenses'] || 0);
    });

    const netBalance = totalIncome - totalExpenses;
    const profitMargin = totalBudget > 0 ? (netBalance / totalBudget) * 100 : 0;
    const expenseUsagePercent = totalBudget > 0 ? (totalExpenses / totalBudget) * 100 : 0;

    return {
      totalProjects: rows.length,
      totalBudget,
      totalIncome,
      totalExpenses,
      netBalance,
      profitMargin,
      expenseUsagePercent
    };
  }, [reportType, reportData]);

  // Bar chart data for Project Financials
  const barChartData = useMemo(() => {
    if (reportType !== 'PROJECT' || !reportData || !Array.isArray(reportData.rows) || reportData.rows.length === 0) return null;

    if (selectedProjectId || reportData.rows.length === 1) {
      const activeRow = selectedProjectId
        ? reportData.rows.find(r => String(r['ID']) === String(selectedProjectId))
        : reportData.rows[0];

      const budget = Number(activeRow?.['Budget'] || 0);
      const income = Number(activeRow?.['Total Income'] || 0);
      const expenses = Number(activeRow?.['Total Expenses'] || 0);
      const balance = income - expenses;

      return {
        labels: ['Budget', 'Total Income', 'Total Expenses', 'Net Balance'],
        datasets: [
          {
            label: activeRow?.['Name'] || 'Project Financials',
            data: [budget, income, expenses, balance],
            backgroundColor: [
              'rgba(59, 130, 246, 0.85)',
              'rgba(16, 185, 129, 0.85)',
              'rgba(239, 68, 68, 0.85)',
              balance >= 0 ? 'rgba(139, 92, 246, 0.85)' : 'rgba(249, 115, 22, 0.85)'
            ],
            borderColor: [
              '#2563eb',
              '#059669',
              '#dc2626',
              balance >= 0 ? '#7c3aed' : '#ea580c'
            ],
            borderWidth: 1.5,
            borderRadius: 6
          }
        ]
      };
    }

    // Multiple projects comparison (top 6 projects)
    const topProjects = reportData.rows.slice(0, 6);
    return {
      labels: topProjects.map(r => r['Name']?.length > 14 ? r['Name'].substring(0, 14) + '…' : r['Name']),
      datasets: [
        {
          label: 'Budget',
          data: topProjects.map(r => Number(r['Budget'] || 0)),
          backgroundColor: 'rgba(59, 130, 246, 0.85)',
          borderRadius: 4
        },
        {
          label: 'Income',
          data: topProjects.map(r => Number(r['Total Income'] || 0)),
          backgroundColor: 'rgba(16, 185, 129, 0.85)',
          borderRadius: 4
        },
        {
          label: 'Expenses',
          data: topProjects.map(r => Number(r['Total Expenses'] || 0)),
          backgroundColor: 'rgba(239, 68, 68, 0.85)',
          borderRadius: 4
        }
      ]
    };
  }, [reportType, reportData, selectedProjectId]);

  // Doughnut chart: Income vs Expenses vs Remaining Budget
  const doughnutChartData = useMemo(() => {
    if (reportType !== 'PROJECT' || !reportData) return null;

    let income = projectMetrics.totalIncome;
    let expenses = projectMetrics.totalExpenses;
    let budget = projectMetrics.totalBudget;

    if (selectedProjectId) {
      const activeRow = reportData.rows?.find(r => String(r['ID']) === String(selectedProjectId));
      if (activeRow) {
        budget = Number(activeRow['Budget'] || 0);
        income = Number(activeRow['Total Income'] || 0);
        expenses = Number(activeRow['Total Expenses'] || 0);
      }
    }

    const remainingBudget = Math.max(0, budget - expenses);

    return {
      labels: ['Total Income', 'Total Expenses', 'Remaining Budget'],
      datasets: [
        {
          data: [income, expenses, remainingBudget],
          backgroundColor: [
            'rgba(16, 185, 129, 0.85)',
            'rgba(239, 68, 68, 0.85)',
            'rgba(59, 130, 246, 0.85)'
          ],
          borderColor: ['#059669', '#dc2626', '#2563eb'],
          borderWidth: 1.5
        }
      ]
    };
  }, [reportType, reportData, selectedProjectId, projectMetrics]);

  // Category expense breakdown
  const categoryChartData = useMemo(() => {
    if (reportType !== 'PROJECT') return null;

    const catMap = {};
    if (projectExpenses.length > 0) {
      projectExpenses.forEach(e => {
        const cat = e.category || 'General';
        catMap[cat] = (catMap[cat] || 0) + Number(e.amount || 0);
      });
    } else if (projectTransactions.length > 0) {
      projectTransactions
        .filter(t => t.transactionType === 'EXPENSE' || t.transactionType === 'PURCHASE')
        .forEach(t => {
          const cat = t.accountHeadName || t.category || 'General';
          catMap[cat] = (catMap[cat] || 0) + Number(t.amount || 0);
        });
    }

    const labels = Object.keys(catMap);
    if (labels.length === 0) return null;

    const palette = ['#3b82f6', '#10b981', '#f59e0b', '#ec4899', '#8b5cf6', '#06b6d4', '#64748b', '#e11d48', '#84cc16'];

    return {
      labels,
      datasets: [
        {
          data: Object.values(catMap),
          backgroundColor: labels.map((_, i) => palette[i % palette.length]),
          borderWidth: 1.5
        }
      ]
    };
  }, [reportType, projectExpenses, projectTransactions]);

  // Filtered transactions for the selected project
  const filteredTransactions = useMemo(() => {
    if (!projectTransactions || projectTransactions.length === 0) return [];
    return projectTransactions.filter(t => {
      if (txnTypeFilter !== 'ALL' && t.transactionType !== txnTypeFilter) return false;
      if (txnSearchTerm) {
        const q = txnSearchTerm.toLowerCase();
        const party = String(t.party || t.customerName || t.vendorName || t.payeeName || '').toLowerCase();
        const notes = String(t.notes || t.description || '').toLowerCase();
        const head = String(t.accountHead || t.accountHeadName || '').toLowerCase();
        const voucher = String(t.voucherNo || t.id || '').toLowerCase();
        return party.includes(q) || notes.includes(q) || head.includes(q) || voucher.includes(q);
      }
      return true;
    });
  }, [projectTransactions, txnTypeFilter, txnSearchTerm]);

  // Transactions financial totals
  const txnStats = useMemo(() => {
    let incomeTotal = 0;
    let expenseTotal = 0;
    projectTransactions.forEach(t => {
      const amt = Number(t.amount || 0);
      if (t.transactionType === 'INCOME') {
        incomeTotal += amt;
      } else if (['EXPENSE', 'PURCHASE', 'PAYMENT', 'ADVANCE'].includes(t.transactionType)) {
        expenseTotal += amt;
      }
    });
    return {
      count: projectTransactions.length,
      incomeTotal,
      expenseTotal,
      netFlow: incomeTotal - expenseTotal
    };
  }, [projectTransactions]);

  // WBS Tasks filtered and stats
  const displayWbs = useMemo(() => {
    let list = [];
    if (projectWbsItems.length > 0) list = projectWbsItems;
    else if (reportData?.workBreakdown && Array.isArray(reportData.workBreakdown)) list = reportData.workBreakdown;

    return list.filter(item => {
      const matchesStatus = wbsStatusFilter === 'ALL' || item.status === wbsStatusFilter;
      if (!matchesStatus) return false;
      if (wbsSearchTerm) {
        const q = wbsSearchTerm.toLowerCase();
        const task = String(item.taskName || '').toLowerCase();
        const assigned = String(item.assignedTo || item.assignedToName || '').toLowerCase();
        const desc = String(item.description || '').toLowerCase();
        return task.includes(q) || assigned.includes(q) || desc.includes(q);
      }
      return true;
    });
  }, [projectWbsItems, reportData, wbsStatusFilter, wbsSearchTerm]);

  const wbsStats = useMemo(() => {
    const list = projectWbsItems.length > 0 ? projectWbsItems : (reportData?.workBreakdown || []);
    const total = list.length;
    const completed = list.filter(w => w.status === 'COMPLETED').length;
    const inProgress = list.filter(w => w.status === 'IN_PROGRESS').length;
    const notStarted = list.filter(w => w.status === 'NOT_STARTED').length;
    const blocked = list.filter(w => w.status === 'BLOCKED').length;
    const completionPct = total > 0 ? Math.round((completed / total) * 100) : 0;
    return { total, completed, inProgress, notStarted, blocked, completionPct };
  }, [projectWbsItems, reportData]);

  // Supervisor Updates filtered and stats
  const displaySups = useMemo(() => {
    let list = [];
    if (projectSupervisorUpdates.length > 0) list = projectSupervisorUpdates;
    else if (reportData?.supervisorUpdates && Array.isArray(reportData.supervisorUpdates)) list = reportData.supervisorUpdates;

    return list.filter(item => {
      if (supSearchTerm) {
        const q = supSearchTerm.toLowerCase();
        const sup = String(item.supervisorName || '').toLowerCase();
        const desc = String(item.description || '').toLowerCase();
        const remarks = String(item.reviewComment || '').toLowerCase();
        return sup.includes(q) || desc.includes(q) || remarks.includes(q);
      }
      return true;
    });
  }, [projectSupervisorUpdates, reportData, supSearchTerm]);

  const supStats = useMemo(() => {
    const list = projectSupervisorUpdates.length > 0 ? projectSupervisorUpdates : (reportData?.supervisorUpdates || []);
    const total = list.length;
    const latestProgress = list.length > 0 ? (list[0].workProgress ?? 0) : 0;
    const approved = list.filter(s => s.approvedBy && s.approvedBy !== 'Pending Approval' && s.approvedBy !== '-').length;
    const pending = total - approved;
    return { total, latestProgress, approved, pending };
  }, [projectSupervisorUpdates, reportData]);

  // Material Stocks & Movements filtered and stats
  const displayMaterialStocks = useMemo(() => {
    let list = [];
    if (projectMaterialStocks.length > 0) list = projectMaterialStocks;
    else if (reportData?.materialStocks && Array.isArray(reportData.materialStocks)) list = reportData.materialStocks;

    if (selectedProjectId) {
      list = list.filter(m => String(m.projectId) === String(selectedProjectId));
    }
    if (matSearchTerm.trim()) {
      const q = matSearchTerm.toLowerCase();
      list = list.filter(m => {
        const name = String(m.itemName || '').toLowerCase();
        const code = String(m.itemCode || '').toLowerCase();
        const proj = String(m.projectName || '').toLowerCase();
        return name.includes(q) || code.includes(q) || proj.includes(q);
      });
    }
    return list;
  }, [projectMaterialStocks, reportData, selectedProjectId, matSearchTerm]);

  const displayMaterialHistory = useMemo(() => {
    let list = [];
    if (projectMaterialHistory.length > 0) list = projectMaterialHistory;
    else if (reportData?.materialHistory && Array.isArray(reportData.materialHistory)) list = reportData.materialHistory;

    if (selectedProjectId) {
      list = list.filter(m => String(m.projectId) === String(selectedProjectId));
    }
    if (matTypeFilter !== 'ALL') {
      list = list.filter(m => m.activityType === matTypeFilter);
    }
    if (matSearchTerm.trim()) {
      const q = matSearchTerm.toLowerCase();
      list = list.filter(m => {
        const name = String(m.itemName || '').toLowerCase();
        const code = String(m.itemCode || '').toLowerCase();
        const proj = String(m.projectName || '').toLowerCase();
        const notes = String(m.notes || m.reason || '').toLowerCase();
        const src = String(m.source || '').toLowerCase();
        const dst = String(m.destination || '').toLowerCase();
        return name.includes(q) || code.includes(q) || proj.includes(q) || notes.includes(q) || src.includes(q) || dst.includes(q);
      });
    }
    return list;
  }, [projectMaterialHistory, reportData, selectedProjectId, matTypeFilter, matSearchTerm]);

  const materialStats = useMemo(() => {
    let stocks = projectMaterialStocks.length > 0 ? projectMaterialStocks : (reportData?.materialStocks || []);
    let history = projectMaterialHistory.length > 0 ? projectMaterialHistory : (reportData?.materialHistory || []);

    if (selectedProjectId) {
      stocks = stocks.filter(m => String(m.projectId) === String(selectedProjectId));
      history = history.filter(m => String(m.projectId) === String(selectedProjectId));
    }

    const totalItems = stocks.length;
    const totalAvailable = stocks.reduce((sum, s) => sum + (Number(s.availableQuantity) || 0), 0);
    const totalValuation = stocks.reduce((sum, s) => sum + (Number(s.totalValue) || 0), 0);
    const totalMovements = history.length;
    const inwardCount = history.filter(h => h.activityType === 'INWARD').length;
    const consumptionCount = history.filter(h => h.activityType === 'CONSUMPTION').length;
    const outwardCount = history.filter(h => h.activityType === 'OUTWARD').length;

    return { totalItems, totalAvailable, totalValuation, totalMovements, inwardCount, consumptionCount, outwardCount };
  }, [projectMaterialStocks, projectMaterialHistory, reportData, selectedProjectId]);

  // Daily Report filtered lists
  const displayDailySups = useMemo(() => {
    if (!reportData?.supervisorUpdates || !Array.isArray(reportData.supervisorUpdates)) return [];
    if (!dailySupSearch) return reportData.supervisorUpdates;
    const q = dailySupSearch.toLowerCase();
    return reportData.supervisorUpdates.filter(s => {
      const proj = String(s.projectName || '').toLowerCase();
      const sup = String(s.supervisorName || '').toLowerCase();
      const desc = String(s.description || '').toLowerCase();
      const rem = String(s.reviewComment || '').toLowerCase();
      return proj.includes(q) || sup.includes(q) || desc.includes(q) || rem.includes(q);
    });
  }, [reportData, dailySupSearch]);

  const displayDailyTimesheets = useMemo(() => {
    if (!reportData?.timesheets || !Array.isArray(reportData.timesheets)) return [];
    if (!dailyTimeSearch) return reportData.timesheets;
    const q = dailyTimeSearch.toLowerCase();
    return reportData.timesheets.filter(t => {
      const code = String(t.employeeCode || '').toLowerCase();
      const name = String(t.employeeName || '').toLowerCase();
      const proj = String(t.projectName || '').toLowerCase();
      const desc = String(t.description || '').toLowerCase();
      return code.includes(q) || name.includes(q) || proj.includes(q) || desc.includes(q);
    });
  }, [reportData, dailyTimeSearch]);

  const displayDailyTransactions = useMemo(() => {
    if (!reportData?.transactions || !Array.isArray(reportData.transactions)) return [];
    return reportData.transactions.filter(t => {
      const matchesType = dailyTxnType === 'ALL' || String(t.transactionType).toUpperCase() === dailyTxnType;
      if (!matchesType) return false;
      if (dailyTxnSearch) {
        const q = dailyTxnSearch.toLowerCase();
        const num = String(t.transactionNumber || '').toLowerCase();
        const party = String(t.partyName || '').toLowerCase();
        const proj = String(t.projectName || '').toLowerCase();
        const head = String(t.accountHead || '').toLowerCase();
        const desc = String(t.description || '').toLowerCase();
        return num.includes(q) || party.includes(q) || proj.includes(q) || head.includes(q) || desc.includes(q);
      }
      return true;
    });
  }, [reportData, dailyTxnType, dailyTxnSearch]);

  const displayDailyAttendance = useMemo(() => {
    if (!reportData?.attendance || !Array.isArray(reportData.attendance)) return [];
    return reportData.attendance.filter(a => {
      const matchesStatus = dailyAttStatus === 'ALL' || String(a.status).toUpperCase() === dailyAttStatus;
      if (!matchesStatus) return false;
      if (dailyAttSearch) {
        const q = dailyAttSearch.toLowerCase();
        const code = String(a.employeeCode || '').toLowerCase();
        const name = String(a.employeeName || '').toLowerCase();
        const dept = String(a.department || '').toLowerCase();
        return code.includes(q) || name.includes(q) || dept.includes(q);
      }
      return true;
    });
  }, [reportData, dailyAttStatus, dailyAttSearch]);

  const displayDailyStocks = useMemo(() => {
    if (!reportData?.materialStocks || !Array.isArray(reportData.materialStocks)) return [];
    if (!dailyStockSearch) return reportData.materialStocks;
    const q = dailyStockSearch.toLowerCase();
    return reportData.materialStocks.filter(s => {
      const name = String(s.itemName || '').toLowerCase();
      const code = String(s.itemCode || '').toLowerCase();
      const proj = String(s.projectName || '').toLowerCase();
      return name.includes(q) || code.includes(q) || proj.includes(q);
    });
  }, [reportData, dailyStockSearch]);

  const displayDailyMovements = useMemo(() => {
    if (!reportData?.materialHistory || !Array.isArray(reportData.materialHistory)) return [];
    if (!dailyStockSearch) return reportData.materialHistory;
    const q = dailyStockSearch.toLowerCase();
    return reportData.materialHistory.filter(m => {
      const name = String(m.itemName || '').toLowerCase();
      const code = String(m.itemCode || '').toLowerCase();
      const proj = String(m.projectName || '').toLowerCase();
      const notes = String(m.notes || m.reason || '').toLowerCase();
      return name.includes(q) || code.includes(q) || proj.includes(q) || notes.includes(q);
    });
  }, [reportData, dailyStockSearch]);

  const dailyStockStats = useMemo(() => {
    const stocks = reportData?.materialStocks && Array.isArray(reportData.materialStocks) ? reportData.materialStocks : [];
    const movements = reportData?.materialHistory && Array.isArray(reportData.materialHistory) ? reportData.materialHistory : [];
    const totalItems = stocks.length;
    const totalAvailable = stocks.reduce((sum, s) => sum + (Number(s.availableQuantity) || 0), 0);
    const totalValuation = stocks.reduce((sum, s) => sum + (Number(s.totalValue) || 0), 0);
    const inwardQty = movements.filter(m => m.activityType === 'INWARD').reduce((sum, m) => sum + (Number(m.quantity) || 0), 0);
    const consumedQty = movements.filter(m => m.activityType === 'CONSUMPTION').reduce((sum, m) => sum + (Number(m.quantity) || 0), 0);
    return { totalItems, totalAvailable, totalValuation, inwardQty, consumedQty };
  }, [reportData]);

  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: { font: { size: 12, weight: '600' }, padding: 12 }
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const val = context.parsed.y !== undefined ? context.parsed.y : context.parsed;
            return ` ${context.dataset.label || context.label}: ${formatCurrency(val)}`;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: (value) => {
            if (Math.abs(value) >= 10000000) return (value / 10000000).toFixed(1) + 'Cr';
            if (Math.abs(value) >= 100000) return (value / 100000).toFixed(1) + 'L';
            if (Math.abs(value) >= 1000) return (value / 1000).toFixed(0) + 'k';
            return value;
          }
        }
      }
    }
  };

  const doughnutChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: { boxWidth: 12, padding: 10, font: { size: 12, weight: '500' } }
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const val = context.raw || 0;
            return ` ${context.label}: ${formatCurrency(val)}`;
          }
        }
      }
    }
  };
  
  return (
    <div className="reports-page">
      <div className="page-header">
        <h1>Reports</h1>
      </div>
      
      <div className="report-controls modern-report-card">
        {/* TOP BAR: Report Type on the Left, Action Buttons on the Right */}
        <div className="report-controls-header">
          <div className="report-type-selection">
            <label htmlFor="report-type-select">Report Type</label>
            <div className="select-container">
              <select
                id="report-type-select"
                value={reportType}
                onChange={(e) => {
                  setReportType(e.target.value);
                  setReportData(null);
                }}
                className="report-type-select"
              >
                <option value="PROJECT">🏗️ Project Report</option>
                <option value="DAILY">📅 Daily Report (DPR)</option>
                <option value="ENQUIRY">📋 Enquiry Report</option>
                <option value="EXPENSE">💰 Expense Report</option>
                <option value="PAYROLL">👥 Payroll Report</option>
                <option value="PROFITABILITY">📈 Profitability Report</option>
              </select>
            </div>
          </div>
          
          <div className="action-buttons-group">
            <button
              type="button"
              onClick={handleGenerateReport}
              disabled={loading}
              className="btn btn-primary btn-generate"
              title="Generate report in browser"
            >
              <span className="btn-icon">🔍</span>
              <span>{loading ? 'Generating...' : 'Generate Report'}</span>
            </button>
            
            <button
              type="button"
              onClick={handleExportPdf}
              disabled={loading}
              className="btn btn-export-pdf"
              title="Export report to PDF"
            >
              <span className="btn-icon">📄</span>
              <span>Export PDF</span>
            </button>

            <button
              type="button"
              onClick={handleExportExcel}
              disabled={loading}
              className="btn btn-export-excel"
              title="Export report to Excel (.xlsx)"
            >
              <span className="btn-icon">📊</span>
              <span>Export Excel</span>
            </button>
          </div>
        </div>

        {/* BOTTOM SECTION: Clean, balanced filter parameters */}
        <div className="report-controls-filters">
          <div className="filters-label-row">
            <span className="filters-heading">Filter Parameters</span>
            <span className="filters-hint">Adjust criteria to narrow down report records</span>
          </div>
          <div className="filters-body">
            {renderFilters()}
          </div>
        </div>
      </div>

      {loading && (
        <div className="report-loading-container">
          <div className="loading-spinner"></div>
          <p className="loading-text">Generating {reportType.toLowerCase()} report & compiling data...</p>
        </div>
      )}

      {!reportData && !loading && (
        <div className="report-welcome-placeholder">
          <div className="placeholder-icon">📑</div>
          <h3>Ready to Generate Report</h3>
          <p>
            Choose your criteria above and click <strong>Generate Report</strong> to analyze online,
            or click <strong>Export PDF</strong> / <strong>Export Excel</strong> to download immediately.
          </p>
          <div className="placeholder-badges">
            <span className="p-badge">📊 Financial Charts</span>
            <span className="p-badge">💳 Project Transactions</span>
            <span className="p-badge">📄 Formatted PDF Reports</span>
            <span className="p-badge">📈 Detailed Excel Worksheets</span>
          </div>
        </div>
      )}
      
      {reportData && reportType === 'PROJECT' && (
        <div className="report-results project-report-dashboard">
          <div className="report-metadata">
            {reportData.companyName && (
              <p><strong>Company:</strong> {reportData.companyName}</p>
            )}
            <p><strong>Report Type:</strong> Project Financial & Progress Report</p>
            <p><strong>Generated At:</strong> {reportData.generatedAt}</p>
            <p><strong>Generated By:</strong> {reportData.generatedBy}</p>
            {selectedProjectName && (
              <p className="highlight-tag"><strong>Active Project Focus:</strong> {selectedProjectName}</p>
            )}
          </div>

          {/* KPI Financial Overview Cards */}
          <div className="project-kpi-grid">
            <div className="project-kpi-card budget">
              <div className="kpi-icon">💼</div>
              <div className="kpi-info">
                <span className="kpi-title">Total Budget</span>
                <span className="kpi-value">{formatCurrency(projectMetrics.totalBudget)}</span>
                <span className="kpi-subtext">{projectMetrics.totalProjects} project{projectMetrics.totalProjects === 1 ? '' : 's'} included</span>
              </div>
            </div>

            <div className="project-kpi-card income">
              <div className="kpi-icon">💵</div>
              <div className="kpi-info">
                <span className="kpi-title">Total Income (Receipts)</span>
                <span className="kpi-value text-green">{formatCurrency(projectMetrics.totalIncome)}</span>
                <span className="kpi-subtext">Customer receipts recorded</span>
              </div>
            </div>

            <div className="project-kpi-card expense">
              <div className="kpi-icon">💸</div>
              <div className="kpi-info">
                <span className="kpi-title">Total Expenses</span>
                <span className="kpi-value text-red">{formatCurrency(projectMetrics.totalExpenses)}</span>
                <span className="kpi-subtext">Direct costs & purchases</span>
              </div>
            </div>

            <div className="project-kpi-card balance">
              <div className="kpi-icon">📈</div>
              <div className="kpi-info">
                <span className="kpi-title">Net Balance</span>
                <span className={`kpi-value ${projectMetrics.netBalance >= 0 ? 'text-green' : 'text-red'}`}>
                  {formatCurrency(projectMetrics.netBalance)}
                </span>
                <span className="kpi-subtext">Income minus expenses</span>
              </div>
            </div>

            <div className="project-kpi-card margin">
              <div className="kpi-icon">📊</div>
              <div className="kpi-info">
                <span className="kpi-title">Profit Margin</span>
                <span className={`kpi-value ${projectMetrics.profitMargin >= 0 ? 'text-purple' : 'text-orange'}`}>
                  {projectMetrics.profitMargin.toFixed(1)}%
                </span>
                <span className="kpi-subtext">{projectMetrics.expenseUsagePercent.toFixed(1)}% budget utilized</span>
              </div>
            </div>
          </div>

          {/* Navigation Tabs */}
          <div className="project-report-tabs">
            <button
              type="button"
              className={`project-report-tab ${activeProjectTab === 'analytics' ? 'active' : ''}`}
              onClick={() => setActiveProjectTab('analytics')}
            >
              📊 Financial Analytics & Charts
            </button>
            <button
              type="button"
              className={`project-report-tab ${activeProjectTab === 'projects' ? 'active' : ''}`}
              onClick={() => setActiveProjectTab('projects')}
            >
              🏗️ Projects List ({reportData.rows.length})
            </button>
            <button
              type="button"
              className={`project-report-tab ${activeProjectTab === 'transactions' ? 'active' : ''}`}
              onClick={() => setActiveProjectTab('transactions')}
            >
              💳 Project Transactions ({projectTransactions.length})
            </button>
            <button
              type="button"
              className={`project-report-tab ${activeProjectTab === 'wbs' ? 'active' : ''}`}
              onClick={() => setActiveProjectTab('wbs')}
            >
              📋 Work Breakdown (WBS) ({wbsStats.total})
            </button>
            <button
              type="button"
              className={`project-report-tab ${activeProjectTab === 'supervisor' ? 'active' : ''}`}
              onClick={() => setActiveProjectTab('supervisor')}
            >
              👷 Supervisor Updates ({supStats.total})
            </button>
            <button
              type="button"
              className={`project-report-tab ${activeProjectTab === 'expenses' ? 'active' : ''}`}
              onClick={() => setActiveProjectTab('expenses')}
            >
              💰 Project Expenses ({projectExpenses.length})
            </button>
            <button
              type="button"
              className={`project-report-tab ${activeProjectTab === 'materials' ? 'active' : ''}`}
              onClick={() => setActiveProjectTab('materials')}
            >
              📦 Site Stock Details & History ({materialStats.totalItems > 0 ? materialStats.totalItems : materialStats.totalMovements})
            </button>
          </div>

          {/* TAB 1: ANALYTICS & CHARTS */}
          {activeProjectTab === 'analytics' && (
            <div className="project-tab-content">
              {reportData.rows.length > 1 && (
                <div className="project-focus-bar">
                  <span><strong>Inspect Project:</strong></span>
                  <select
                    value={selectedProjectId || ''}
                    onChange={(e) => {
                      const pid = e.target.value;
                      if (!pid) {
                        setSelectedProjectId(null);
                        setSelectedProjectName('');
                        setProjectTransactions([]);
                        setProjectExpenses([]);
                      } else {
                        const row = reportData.rows.find(r => String(r['ID']) === String(pid));
                        fetchProjectDetails(pid, row ? row['Name'] : '');
                      }
                    }}
                    className="project-focus-select"
                  >
                    <option value="">Overview (All Projects)</option>
                    {reportData.rows.map(r => (
                      <option key={r['ID']} value={r['ID']}>
                        {r['Name']} ({formatCurrency(r['Budget'])})
                      </option>
                    ))}
                  </select>
                  {selectedProjectName && (
                    <span className="project-focus-badge">Focused: {selectedProjectName}</span>
                  )}
                </div>
              )}

              <div className="project-charts-grid">
                {barChartData && (
                  <div className="project-chart-card wide">
                    <div className="chart-header">
                      <h4>📊 Financial Comparison: Budget vs Income vs Expenses</h4>
                      <span className="chart-subtext">
                        {selectedProjectName ? `Detailed financials for ${selectedProjectName}` : 'Comparison of top projects in report'}
                      </span>
                    </div>
                    <div className="chart-canvas-container">
                      <Bar data={barChartData} options={barChartOptions} />
                    </div>
                  </div>
                )}

                {doughnutChartData && (
                  <div className="project-chart-card">
                    <div className="chart-header">
                      <h4>🍩 Cash Flow & Budget Allocation</h4>
                      <span className="chart-subtext">Income received, expenses spent, remaining budget</span>
                    </div>
                    <div className="chart-canvas-container">
                      <Doughnut data={doughnutChartData} options={doughnutChartOptions} />
                    </div>
                  </div>
                )}

                {categoryChartData && (
                  <div className="project-chart-card">
                    <div className="chart-header">
                      <h4>🏷️ Expense Breakdown by Category</h4>
                      <span className="chart-subtext">
                        {selectedProjectName ? `Expenses for ${selectedProjectName}` : 'Categorized project expenditure'}
                      </span>
                    </div>
                    <div className="chart-canvas-container">
                      <Doughnut data={categoryChartData} options={doughnutChartOptions} />
                    </div>
                  </div>
                )}
              </div>

              {/* Transactions Section directly in Dashboard View */}
              <div className="project-txns-section inline-dashboard-txns" style={{ marginTop: '24px' }}>
                <div className="txns-header-bar">
                  <div className="txns-title-wrap">
                    <h3>💳 Project Transactions (Income & Expenses): {selectedProjectName || 'All Records'}</h3>
                    <span className="txns-count-badge">
                      {projectTransactions.length} transaction{projectTransactions.length === 1 ? '' : 's'}
                    </span>
                  </div>

                  <div className="txn-stats-chips">
                    <div className="txn-chip income">Total Income: {formatCurrency(txnStats.incomeTotal)}</div>
                    <div className="txn-chip expense">Total Expenses: {formatCurrency(txnStats.expenseTotal)}</div>
                    <div className={`txn-chip ${txnStats.netFlow >= 0 ? 'net-positive' : 'net-negative'}`}>
                      Net Flow: {formatCurrency(txnStats.netFlow)}
                    </div>
                  </div>
                </div>

                <div className="txns-toolbar">
                  <div className="txn-type-pills">
                    {['ALL', 'INCOME', 'EXPENSE', 'PURCHASE', 'PAYMENT'].map(type => (
                      <button
                        key={type}
                        type="button"
                        className={`txn-pill ${txnTypeFilter === type ? 'active' : ''}`}
                        onClick={() => setTxnTypeFilter(type)}
                      >
                        {type}
                      </button>
                    ))}
                  </div>

                  <div className="txns-search-wrap">
                    <input
                      type="text"
                      placeholder="Search payee, account, notes..."
                      value={txnSearchTerm}
                      onChange={(e) => setTxnSearchTerm(e.target.value)}
                      className="txns-search-input"
                    />
                    {txnSearchTerm && (
                      <button type="button" className="txns-search-clear" onClick={() => setTxnSearchTerm('')}>✕</button>
                    )}
                  </div>
                </div>

                {projectDetailsLoading ? (
                  <div className="loading-state">Loading transactions...</div>
                ) : filteredTransactions.length === 0 ? (
                  <div className="empty-project-detail">
                    <div className="empty-icon">💳</div>
                    <p>No financial transactions found for the selected criteria.</p>
                  </div>
                ) : (
                  <div className="report-table-container">
                    <table className="report-table transactions-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Voucher #</th>
                          <th>Type</th>
                          <th>Account Head</th>
                          <th>Customer / Vendor / Payee</th>
                          <th>Amount</th>
                          <th>Status</th>
                          <th>Notes / Description</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredTransactions.map((txn) => {
                          const isIncome = txn.transactionType === 'INCOME';
                          return (
                            <tr key={txn.id}>
                              <td>{txn.transactionDate ? new Date(txn.transactionDate).toLocaleDateString() : '-'}</td>
                              <td className="font-mono">{txn.voucherNo || `#${txn.id}`}</td>
                              <td>
                                <span className={`txn-badge badge-${(txn.transactionType || '').toLowerCase()}`}>
                                  {txn.transactionType}
                                </span>
                              </td>
                              <td>{txn.accountHead || txn.accountHeadName || '-'}</td>
                              <td>{txn.party || txn.customerName || txn.vendorName || txn.payeeName || '-'}</td>
                              <td className={isIncome ? 'text-green font-bold' : 'text-red font-bold'}>
                                {isIncome ? '+' : '-'}{formatCurrency(txn.amount)}
                              </td>
                              <td>
                                <span className={`status-badge status-${(txn.status || 'APPROVED').toLowerCase()}`}>
                                  {txn.status || 'APPROVED'}
                                </span>
                              </td>
                              <td className="txn-notes-cell">{txn.notes || txn.description || '-'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* TAB 2: PROJECTS SUMMARY TABLE */}
          {activeProjectTab === 'projects' && (
            <div className="project-tab-content">
              <div className="report-table-container">
                <table className="report-table">
                  <thead>
                    <tr>
                      {reportData.columns.map((column, index) => (
                        <th key={index}>{column}</th>
                      ))}
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.rows.map((row, rowIndex) => {
                      const isSelected = selectedProjectId && String(row['ID']) === String(selectedProjectId);
                      return (
                        <tr key={rowIndex} className={isSelected ? 'selected-project-row' : ''}>
                          {reportData.columns.map((column, colIndex) => {
                            const val = row[column];
                            const isNetBalance = String(column).toLowerCase().includes('balance');
                            const num = Number(val);
                            return (
                              <td key={colIndex} className={isNetBalance ? (num >= 0 ? 'text-green font-bold' : 'text-red font-bold') : ''}>
                                {formatValue(val, column)}
                              </td>
                            );
                          })}
                          <td>
                            <button
                              type="button"
                              className="btn-inspect-project"
                              onClick={() => {
                                fetchProjectDetails(row['ID'], row['Name']);
                                setActiveProjectTab('analytics');
                              }}
                              title="View analytics & transactions for this project"
                            >
                              🔍 View Analytics
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* TAB 3: PROJECT TRANSACTIONS */}
          {activeProjectTab === 'transactions' && (
            <div className="project-tab-content">
              {projectDetailsLoading ? (
                <div className="loading-state">Loading transactions...</div>
              ) : projectTransactions.length === 0 ? (
                <div className="empty-project-detail">
                  <div className="empty-icon">💳</div>
                  <p>No financial transactions found for {selectedProjectName || 'this project'}.</p>
                  {reportData.rows.length > 1 && !selectedProjectId && (
                    <p className="empty-hint">Click on a project in the Projects List tab to view its transactions.</p>
                  )}
                </div>
              ) : (
                <div className="project-txns-section">
                  <div className="txns-header-bar">
                    <div className="txns-title-wrap">
                      <h3>Financial Transactions: {selectedProjectName}</h3>
                      <span className="txns-count-badge">{projectTransactions.length} transaction{projectTransactions.length === 1 ? '' : 's'}</span>
                    </div>

                    <div className="txn-stats-chips">
                      <div className="txn-chip income">Income: {formatCurrency(txnStats.incomeTotal)}</div>
                      <div className="txn-chip expense">Expenses: {formatCurrency(txnStats.expenseTotal)}</div>
                      <div className={`txn-chip ${txnStats.netFlow >= 0 ? 'net-positive' : 'net-negative'}`}>
                        Net Cash Flow: {formatCurrency(txnStats.netFlow)}
                      </div>
                    </div>
                  </div>

                  <div className="txns-toolbar">
                    <div className="txn-type-pills">
                      {['ALL', 'INCOME', 'EXPENSE', 'PURCHASE', 'PAYMENT'].map(type => (
                        <button
                          key={type}
                          type="button"
                          className={`txn-pill ${txnTypeFilter === type ? 'active' : ''}`}
                          onClick={() => setTxnTypeFilter(type)}
                        >
                          {type}
                        </button>
                      ))}
                    </div>

                    <div className="txns-search-wrap">
                      <input
                        type="text"
                        placeholder="Search payee, account, notes..."
                        value={txnSearchTerm}
                        onChange={(e) => setTxnSearchTerm(e.target.value)}
                        className="txns-search-input"
                      />
                      {txnSearchTerm && (
                        <button type="button" className="txns-search-clear" onClick={() => setTxnSearchTerm('')}>✕</button>
                      )}
                    </div>
                  </div>

                  <div className="report-table-container">
                    <table className="report-table transactions-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Voucher #</th>
                          <th>Type</th>
                          <th>Account Head</th>
                          <th>Customer / Vendor / Payee</th>
                          <th>Amount</th>
                          <th>Status</th>
                          <th>Notes / Description</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredTransactions.map((txn) => {
                          const isIncome = txn.transactionType === 'INCOME';
                          return (
                            <tr key={txn.id}>
                              <td>{txn.transactionDate ? new Date(txn.transactionDate).toLocaleDateString() : '-'}</td>
                              <td className="font-mono">{txn.voucherNo || `#${txn.id}`}</td>
                              <td>
                                <span className={`txn-badge badge-${(txn.transactionType || '').toLowerCase()}`}>
                                  {txn.transactionType}
                                </span>
                              </td>
                              <td>{txn.accountHead || txn.accountHeadName || '-'}</td>
                              <td>{txn.party || txn.customerName || txn.vendorName || txn.payeeName || '-'}</td>
                              <td className={isIncome ? 'text-green font-bold' : 'text-red font-bold'}>
                                {isIncome ? '+' : '-'}{formatCurrency(txn.amount)}
                              </td>
                              <td>
                                <span className={`status-badge status-${(txn.status || 'APPROVED').toLowerCase()}`}>
                                  {txn.status || 'APPROVED'}
                                </span>
                              </td>
                              <td className="txn-notes-cell">{txn.notes || '-'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* TAB 4: WORK BREAKDOWN STRUCTURE (WBS) */}
          {activeProjectTab === 'wbs' && (
            <div className="project-tab-content">
              {projectDetailsLoading ? (
                <div className="loading-state">Loading Work Breakdown Structure...</div>
              ) : displayWbs.length === 0 ? (
                <div className="empty-project-detail">
                  <div className="empty-icon">📋</div>
                  <p>No Work Breakdown Structure (WBS) tasks recorded for {selectedProjectName || 'this project'}.</p>
                </div>
              ) : (
                <div className="project-wbs-section">
                  <div className="txns-header-bar">
                    <div className="txns-title-wrap">
                      <h3>📋 Work Breakdown Structure (WBS) & Milestones: {selectedProjectName || 'All Records'}</h3>
                      <span className="txns-count-badge">
                        {wbsStats.total} task{wbsStats.total === 1 ? '' : 's'} ({wbsStats.completionPct}% completed)
                      </span>
                    </div>

                    <div className="txn-stats-chips">
                      <div className="txn-chip income">Completed: {wbsStats.completed}</div>
                      <div className="txn-chip net-positive">In Progress: {wbsStats.inProgress}</div>
                      <div className="txn-chip">Not Started: {wbsStats.notStarted}</div>
                      {wbsStats.blocked > 0 && (
                        <div className="txn-chip net-negative">Blocked: {wbsStats.blocked}</div>
                      )}
                    </div>
                  </div>

                  <div className="txns-toolbar">
                    <div className="txn-type-pills">
                      {['ALL', 'COMPLETED', 'IN_PROGRESS', 'NOT_STARTED', 'BLOCKED'].map(st => (
                        <button
                          key={st}
                          type="button"
                          className={`pill-btn ${wbsStatusFilter === st ? 'active' : ''}`}
                          onClick={() => setWbsStatusFilter(st)}
                        >
                          {st.replace('_', ' ')}
                        </button>
                      ))}
                    </div>

                    <div className="txn-search-wrap">
                      <input
                        type="text"
                        placeholder="Search tasks, assignees, notes..."
                        value={wbsSearchTerm}
                        onChange={(e) => setWbsSearchTerm(e.target.value)}
                        className="txn-search-input"
                      />
                    </div>
                  </div>

                  <div className="report-table-container">
                    <table className="report-table">
                      <thead>
                        <tr>
                          <th>Task Name</th>
                          <th>Assigned To</th>
                          <th>Start Date</th>
                          <th>End Date</th>
                          <th>Status</th>
                          <th>Parent Task</th>
                          <th>Description</th>
                        </tr>
                      </thead>
                      <tbody>
                        {displayWbs.map((task) => (
                          <tr key={task.id}>
                            <td className="font-bold text-slate-800">{task.taskName}</td>
                            <td>{task.assignedTo || task.assignedToName || '-'}</td>
                            <td>{task.startDate ? new Date(task.startDate).toLocaleDateString() : '-'}</td>
                            <td>{task.endDate ? new Date(task.endDate).toLocaleDateString() : '-'}</td>
                            <td>
                              <span className={`wbs-badge badge-${(task.status || '').toLowerCase().replace('_', '-')}`}>
                                {task.status}
                              </span>
                            </td>
                            <td>{task.parentTask || '-'}</td>
                            <td className="txn-notes-cell">{task.description || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* TAB 5: SUPERVISOR SITE PROGRESS UPDATES */}
          {activeProjectTab === 'supervisor' && (
            <div className="project-tab-content">
              {projectDetailsLoading ? (
                <div className="loading-state">Loading Supervisor Updates...</div>
              ) : displaySups.length === 0 ? (
                <div className="empty-project-detail">
                  <div className="empty-icon">👷</div>
                  <p>No supervisor updates recorded for {selectedProjectName || 'this project'}.</p>
                </div>
              ) : (
                <div className="project-supervisor-section">
                  <div className="txns-header-bar">
                    <div className="txns-title-wrap">
                      <h3>👷 Supervisor Site Progress Updates: {selectedProjectName || 'All Records'}</h3>
                      <span className="txns-count-badge">
                        {supStats.total} update{supStats.total === 1 ? '' : 's'}
                      </span>
                    </div>

                    <div className="txn-stats-chips">
                      <div className="txn-chip income">Latest Progress: {supStats.latestProgress}%</div>
                      <div className="txn-chip net-positive">Approved: {supStats.approved}</div>
                      {supStats.pending > 0 && (
                        <div className="txn-chip net-negative">Pending Review: {supStats.pending}</div>
                      )}
                    </div>
                  </div>

                  <div className="txns-toolbar">
                    <div className="txn-search-wrap" style={{ width: '100%' }}>
                      <input
                        type="text"
                        placeholder="Search supervisor, work description, approval remarks..."
                        value={supSearchTerm}
                        onChange={(e) => setSupSearchTerm(e.target.value)}
                        className="txn-search-input"
                      />
                    </div>
                  </div>

                  <div className="report-table-container">
                    <table className="report-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Supervisor</th>
                          <th style={{ width: '160px' }}>Work Progress</th>
                          <th>Work Completed / Description</th>
                          <th>Approval Status</th>
                          <th>Review Remarks</th>
                        </tr>
                      </thead>
                      <tbody>
                        {displaySups.map((sup) => {
                          const prog = Number(sup.workProgress || 0);
                          const isApproved = sup.approvedBy && sup.approvedBy !== 'Pending Approval' && sup.approvedBy !== '-';
                          return (
                            <tr key={sup.id}>
                              <td className="font-semibold text-slate-700">
                                {sup.updateDate ? new Date(sup.updateDate).toLocaleDateString() : '-'}
                              </td>
                              <td className="font-semibold">{sup.supervisorName || '-'}</td>
                              <td>
                                <div className="sup-progress-bar-wrap">
                                  <div className="sup-progress-track">
                                    <div
                                      className={`sup-progress-fill ${prog >= 80 ? 'fill-green' : prog >= 40 ? 'fill-blue' : 'fill-slate'}`}
                                      style={{ width: `${Math.min(100, Math.max(0, prog))}%` }}
                                    />
                                  </div>
                                  <span className="sup-progress-text">{prog}%</span>
                                </div>
                              </td>
                              <td className="sup-desc-cell">{sup.description || '-'}</td>
                              <td>
                                <span className={`sup-approval-badge ${isApproved ? 'approved' : 'pending'}`}>
                                  {isApproved ? `✓ ${sup.approvedBy}` : '⏳ Pending Review'}
                                </span>
                              </td>
                              <td className="txn-notes-cell">{sup.reviewComment || '-'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* TAB 6: PROJECT EXPENSES */}
          {activeProjectTab === 'expenses' && (
            <div className="project-tab-content">
              {projectDetailsLoading ? (
                <div className="loading-state">Loading expenses...</div>
              ) : projectExpenses.length === 0 ? (
                <div className="empty-project-detail">
                  <div className="empty-icon">💰</div>
                  <p>No itemized expenses found for {selectedProjectName || 'this project'}.</p>
                </div>
              ) : (
                <div className="report-table-container">
                  <table className="report-table">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Category</th>
                        <th>Vendor / Payee</th>
                        <th>Amount</th>
                        <th>Expense Date</th>
                        <th>Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      {projectExpenses.map((exp, idx) => (
                        <tr key={exp.id || idx}>
                          <td>#{exp.id}</td>
                          <td><span className="category-pill">{exp.category || 'General'}</span></td>
                          <td>{exp.vendorName || '-'}</td>
                          <td className="text-red font-bold">{formatCurrency(exp.amount)}</td>
                          <td>{exp.expenseDate ? new Date(exp.expenseDate).toLocaleDateString() : '-'}</td>
                          <td>{exp.description || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* TAB 7: SITE STOCK DETAILS & HISTORY */}
          {activeProjectTab === 'materials' && (
            <div className="project-tab-content">
              {projectDetailsLoading ? (
                <div className="loading-state">Loading site stock details...</div>
              ) : (
                <>
                  {/* Site Stock KPI Overview Cards */}
                  <div className="project-kpis" style={{ marginBottom: '15px' }}>
                    <div className="project-kpi-card budget">
                      <div className="kpi-icon">📦</div>
                      <div className="kpi-info">
                        <span className="kpi-title">Tracked Site Items</span>
                        <span className="kpi-value">{materialStats.totalItems}</span>
                        <span className="kpi-subtext">Allocated materials</span>
                      </div>
                    </div>

                    <div className="project-kpi-card income">
                      <div className="kpi-icon">🟢</div>
                      <div className="kpi-info">
                        <span className="kpi-title">Available Site Stock</span>
                        <span className="kpi-value text-green">{materialStats.totalAvailable.toLocaleString()}</span>
                        <span className="kpi-subtext">Total units on site</span>
                      </div>
                    </div>

                    <div className="project-kpi-card balance">
                      <div className="kpi-icon">💰</div>
                      <div className="kpi-info">
                        <span className="kpi-title">Site Stock Valuation</span>
                        <span className="kpi-value text-purple">{formatCurrency(materialStats.totalValuation)}</span>
                        <span className="kpi-subtext">Based on purchase prices</span>
                      </div>
                    </div>

                    <div className="project-kpi-card margin">
                      <div className="kpi-icon">📜</div>
                      <div className="kpi-info">
                        <span className="kpi-title">Movement Logs</span>
                        <span className="kpi-value">{materialStats.totalMovements}</span>
                        <span className="kpi-subtext">{materialStats.inwardCount} Inward · {materialStats.consumptionCount} Used</span>
                      </div>
                    </div>
                  </div>

                  {/* Sub-view toggle & Search / Filters */}
                  <div className="project-filter-bar" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px', marginBottom: '15px' }}>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        type="button"
                        className={`filter-btn ${matSubTab === 'stocks' ? 'active' : ''}`}
                        onClick={() => setMatSubTab('stocks')}
                        style={{ padding: '6px 14px', borderRadius: '6px', fontWeight: '500', cursor: 'pointer', border: matSubTab === 'stocks' ? '2px solid #2563eb' : '1px solid #cbd5e1', background: matSubTab === 'stocks' ? '#eff6ff' : '#ffffff', color: matSubTab === 'stocks' ? '#1d4ed8' : '#475569' }}
                      >
                        📦 Current Site Stock Levels ({materialStats.totalItems})
                      </button>
                      <button
                        type="button"
                        className={`filter-btn ${matSubTab === 'history' ? 'active' : ''}`}
                        onClick={() => setMatSubTab('history')}
                        style={{ padding: '6px 14px', borderRadius: '6px', fontWeight: '500', cursor: 'pointer', border: matSubTab === 'history' ? '2px solid #2563eb' : '1px solid #cbd5e1', background: matSubTab === 'history' ? '#eff6ff' : '#ffffff', color: matSubTab === 'history' ? '#1d4ed8' : '#475569' }}
                      >
                        📜 Movement & Consumption Log ({materialStats.totalMovements})
                      </button>
                    </div>

                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                      {matSubTab === 'history' && (
                        <select
                          value={matTypeFilter}
                          onChange={(e) => setMatTypeFilter(e.target.value)}
                          className="filter-select"
                          style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px' }}
                        >
                          <option value="ALL">All Activities ({materialStats.totalMovements})</option>
                          <option value="INWARD">📥 Inward Transfers ({materialStats.inwardCount})</option>
                          <option value="CONSUMPTION">🔻 Consumptions ({materialStats.consumptionCount})</option>
                          <option value="OUTWARD">📤 Outward Transfers ({materialStats.outwardCount})</option>
                        </select>
                      )}

                      <div className="search-box">
                        <span className="search-icon">🔍</span>
                        <input
                          type="text"
                          placeholder={matSubTab === 'stocks' ? 'Search stock items...' : 'Search movement history...'}
                          value={matSearchTerm}
                          onChange={(e) => setMatSearchTerm(e.target.value)}
                        />
                        {matSearchTerm && (
                          <button className="clear-btn" onClick={() => setMatSearchTerm('')}>✕</button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Sub-view 1: Current Stock Levels (Site Stock Details) */}
                  {matSubTab === 'stocks' && (
                    displayMaterialStocks.length === 0 ? (
                      <div className="empty-project-detail">
                        <div className="empty-icon">📦</div>
                        <p>No material stocks allocated to {selectedProjectName || 'this project'}.</p>
                      </div>
                    ) : (
                      <div className="report-table-container">
                        <table className="report-table">
                          <thead>
                            <tr>
                              {(reportData.rows.length > 1 && !selectedProjectId) && <th>Project</th>}
                              <th>Item Code</th>
                              <th>Material Name</th>
                              <th>Unit</th>
                              <th style={{ textAlign: 'right' }}>Total Received</th>
                              <th style={{ textAlign: 'right' }}>Total Consumed</th>
                              <th style={{ textAlign: 'right' }}>Available Site Stock</th>
                              <th style={{ textAlign: 'right' }}>Unit Cost</th>
                              <th style={{ textAlign: 'right' }}>Current Valuation</th>
                              <th style={{ textAlign: 'center' }}>Stock Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {displayMaterialStocks.map((s, idx) => (
                              <tr key={s.itemId || idx}>
                                {(reportData.rows.length > 1 && !selectedProjectId) && (
                                  <td><strong>{s.projectName || '-'}</strong></td>
                                )}
                                <td className="font-mono"><strong>{s.itemCode || '-'}</strong></td>
                                <td><strong>{s.itemName || '-'}</strong></td>
                                <td>{s.unitOfMeasurement || '-'}</td>
                                <td style={{ textAlign: 'right' }}>
                                  {Number(s.totalReceived || 0).toLocaleString()}
                                </td>
                                <td style={{ textAlign: 'right', color: '#dc2626', fontWeight: 'bold' }}>
                                  {Number(s.consumedQuantity || 0).toLocaleString()}
                                </td>
                                <td style={{ textAlign: 'right', color: '#16a34a', fontWeight: 'bold' }}>
                                  {Number(s.availableQuantity || 0).toLocaleString()}
                                </td>
                                <td style={{ textAlign: 'right' }}>
                                  {formatCurrency(s.unitPrice || 0)}
                                </td>
                                <td style={{ textAlign: 'right', fontWeight: 'bold' }}>
                                  {formatCurrency(s.totalValue || 0)}
                                </td>
                                <td style={{ textAlign: 'center' }}>
                                  <span className={`status-pill ${Number(s.availableQuantity || 0) > 0 ? 'status-completed' : 'status-blocked'}`}>
                                    {s.statusLabel || (Number(s.availableQuantity || 0) > 0 ? 'Available' : 'Fully Consumed')}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr style={{ background: '#f8fafc', fontWeight: 'bold' }}>
                              <td colSpan={(reportData.rows.length > 1 && !selectedProjectId) ? 6 : 5} style={{ textAlign: 'right' }}>
                                Total Current Site Stock Value:
                              </td>
                              <td style={{ textAlign: 'right', color: '#16a34a' }}>
                                {materialStats.totalAvailable.toLocaleString()} Units
                              </td>
                              <td></td>
                              <td style={{ textAlign: 'right', color: '#2563eb' }}>
                                {formatCurrency(materialStats.totalValuation)}
                              </td>
                              <td></td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    )
                  )}

                  {/* Sub-view 2: Movement & Consumption Log */}
                  {matSubTab === 'history' && (
                    displayMaterialHistory.length === 0 ? (
                      <div className="empty-project-detail">
                        <div className="empty-icon">📜</div>
                        <p>No material movements or consumption records found for {selectedProjectName || 'this project'}.</p>
                      </div>
                    ) : (
                      <div className="report-table-container">
                        <table className="report-table">
                          <thead>
                            <tr>
                              {(reportData.rows.length > 1 && !selectedProjectId) && <th>Project</th>}
                              <th>Date</th>
                              <th>Item Code</th>
                              <th>Material Name</th>
                              <th>Activity Type</th>
                              <th style={{ textAlign: 'right' }}>Quantity</th>
                              <th>Source / From</th>
                              <th>Destination / To</th>
                              <th>Notes / Purpose</th>
                              <th>Moved By</th>
                            </tr>
                          </thead>
                          <tbody>
                            {displayMaterialHistory.map((m, idx) => {
                              const isConsumed = m.activityType === 'CONSUMPTION';
                              const isInward = m.activityType === 'INWARD';
                              return (
                                <tr key={m.id || idx}>
                                  {(reportData.rows.length > 1 && !selectedProjectId) && (
                                    <td><strong>{m.projectName || '-'}</strong></td>
                                  )}
                                  <td className="font-mono">
                                    {m.transactionDate ? new Date(m.transactionDate).toLocaleDateString() : '-'}
                                  </td>
                                  <td className="font-mono"><strong>{m.itemCode || '-'}</strong></td>
                                  <td><strong>{m.itemName || '-'}</strong></td>
                                  <td>
                                    <span className={`status-pill ${isInward ? 'status-completed' : isConsumed ? 'status-in-progress' : 'status-pending'}`}>
                                      {m.activityType || '-'}
                                    </span>
                                  </td>
                                  <td style={{ textAlign: 'right', fontWeight: 'bold', color: isConsumed ? '#dc2626' : isInward ? '#16a34a' : '#1e293b' }}>
                                    {isConsumed ? '-' : '+'}{Number(m.quantity || 0).toLocaleString()} {m.unitOfMeasurement || ''}
                                  </td>
                                  <td style={{ fontSize: '12px', color: '#64748b' }}>
                                    {m.source || '-'}
                                  </td>
                                  <td style={{ fontSize: '12px', color: '#64748b' }}>
                                    {m.destination || '-'}
                                  </td>
                                  <td style={{ fontSize: '12px', color: '#475569' }}>
                                    {m.notes || m.reason || '-'}
                                  </td>
                                  <td>{m.movedBy || '-'}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* DAILY PROGRESS & OPERATIONS REPORT (DPR) DASHBOARD */}
      {reportData && reportType === 'DAILY' && (
        <div className="report-results daily-report-dashboard">
          <div className="report-metadata">
            {reportData.companyName && (
              <p><strong>Company:</strong> {reportData.companyName}</p>
            )}
            <p><strong>Report:</strong> Daily Progress & Operations Report (DPR)</p>
            <p className="highlight-tag"><strong>Date:</strong> {reportData.reportDate || reportData.summary?.['Report Date'] || 'Today'}</p>
            <p><strong>Scope:</strong> {reportData.summary?.['Project Scope'] || 'All Projects'}</p>
            <p><strong>Generated By:</strong> {reportData.generatedBy}</p>
            <p><strong>Generated At:</strong> {reportData.generatedAt}</p>
          </div>

          {/* Daily KPI Metric Cards */}
          <div className="project-kpi-grid daily-kpi-grid">
            <div className="project-kpi-card budget">
              <div className="kpi-icon">👷</div>
              <div className="kpi-info">
                <span className="kpi-title">Workers on Site</span>
                <span className="kpi-value">{reportData.summary?.['Workers Present'] ?? 0}</span>
                <span className="kpi-subtext">Present & half-day attendance</span>
              </div>
            </div>

            <div className="project-kpi-card income">
              <div className="kpi-icon">⏱️</div>
              <div className="kpi-info">
                <span className="kpi-title">Man-Hours Logged</span>
                <span className="kpi-value">{reportData.summary?.['Total Man-Hours'] ?? 0} hrs</span>
                <span className="kpi-subtext">{reportData.timesheets?.length || 0} timesheet records</span>
              </div>
            </div>

            <div className="project-kpi-card balance">
              <div className="kpi-icon">📝</div>
              <div className="kpi-info">
                <span className="kpi-title">Site Progress Logs</span>
                <span className="kpi-value">{reportData.summary?.['Supervisor Updates'] ?? 0}</span>
                <span className="kpi-subtext">Supervisor field updates</span>
              </div>
            </div>

            <div className="project-kpi-card income">
              <div className="kpi-icon">📈</div>
              <div className="kpi-info">
                <span className="kpi-title">Daily Cash Inflow</span>
                <span className="kpi-value text-green">{formatCurrency(reportData.summary?.['Daily Inflow'] ?? 0)}</span>
                <span className="kpi-subtext">Customer & project receipts</span>
              </div>
            </div>

            <div className="project-kpi-card expense">
              <div className="kpi-icon">📉</div>
              <div className="kpi-info">
                <span className="kpi-title">Daily Cash Outflow</span>
                <span className="kpi-value text-red">{formatCurrency(reportData.summary?.['Daily Outflow'] ?? 0)}</span>
                <span className="kpi-subtext">Expenses, labor & purchases</span>
              </div>
            </div>

            <div className={`project-kpi-card ${Number(reportData.summary?.['Net Daily Flow'] ?? 0) >= 0 ? 'balance' : 'expense'}`}>
              <div className="kpi-icon">💵</div>
              <div className="kpi-info">
                <span className="kpi-title">Net Daily Flow</span>
                <span className={`kpi-value ${Number(reportData.summary?.['Net Daily Flow'] ?? 0) >= 0 ? 'text-green' : 'text-red'}`}>
                  {formatCurrency(reportData.summary?.['Net Daily Flow'] ?? 0)}
                </span>
                <span className="kpi-subtext">{Number(reportData.summary?.['Net Daily Flow'] ?? 0) >= 0 ? 'Surplus / Positive' : 'Deficit / Outflow Exceeded'}</span>
              </div>
            </div>

            {reportData.summary?.['Site Stock Items'] !== undefined && (
              <div className="project-kpi-card budget">
                <div className="kpi-icon">📦</div>
                <div className="kpi-info">
                  <span className="kpi-title">Site Stock Valuation</span>
                  <span className="kpi-value text-blue">{formatCurrency(reportData.summary?.['Site Inventory Valuation'] ?? 0)}</span>
                  <span className="kpi-subtext">{reportData.summary?.['Site Stock Items'] ?? 0} items ({reportData.summary?.['Available Site Stock Units'] ?? 0} units)</span>
                </div>
              </div>
            )}
          </div>

          {/* Daily Report Tabs Navigation */}
          <div className="project-detail-tabs">
            <button
              type="button"
              className={`project-tab-btn ${activeDailyTab === 'supervisor' ? 'active' : ''}`}
              onClick={() => setActiveDailyTab('supervisor')}
            >
              <span>👷 Supervisor Site Updates</span>
              <span className="tab-badge">{reportData.supervisorUpdates?.length || 0}</span>
            </button>
            <button
              type="button"
              className={`project-tab-btn ${activeDailyTab === 'timesheets' ? 'active' : ''}`}
              onClick={() => setActiveDailyTab('timesheets')}
            >
              <span>⏱️ Workforce Timesheets</span>
              <span className="tab-badge">{reportData.timesheets?.length || 0}</span>
            </button>
            <button
              type="button"
              className={`project-tab-btn ${activeDailyTab === 'transactions' ? 'active' : ''}`}
              onClick={() => setActiveDailyTab('transactions')}
            >
              <span>💳 Financial Transactions</span>
              <span className="tab-badge">{reportData.transactions?.length || 0}</span>
            </button>
            <button
              type="button"
              className={`project-tab-btn ${activeDailyTab === 'attendance' ? 'active' : ''}`}
              onClick={() => setActiveDailyTab('attendance')}
            >
              <span>📋 Attendance Logs</span>
              <span className="tab-badge">{reportData.attendance?.length || 0}</span>
            </button>
            <button
              type="button"
              className={`project-tab-btn ${activeDailyTab === 'sitestock' ? 'active' : ''}`}
              onClick={() => setActiveDailyTab('sitestock')}
            >
              <span>📦 Site Stock Details</span>
              <span className="tab-badge">{reportData.materialStocks?.length || 0}</span>
            </button>
          </div>

          {/* TAB 1: Supervisor Site Updates */}
          {activeDailyTab === 'supervisor' && (
            <div className="project-tab-content">
              <div className="tab-toolbar">
                <div className="search-box">
                  <span className="search-icon">🔍</span>
                  <input
                    type="text"
                    placeholder="Search updates by supervisor, project, or work description..."
                    value={dailySupSearch}
                    onChange={(e) => setDailySupSearch(e.target.value)}
                  />
                  {dailySupSearch && (
                    <button className="clear-btn" onClick={() => setDailySupSearch('')}>✕</button>
                  )}
                </div>
              </div>

              {displayDailySups.length === 0 ? (
                <div className="empty-project-detail">
                  <div className="empty-icon">👷</div>
                  <p>No supervisor updates recorded for this date.</p>
                </div>
              ) : (
                <div className="report-table-container">
                  <table className="report-table">
                    <thead>
                      <tr>
                        <th>Project</th>
                        <th>Supervisor</th>
                        <th>Progress %</th>
                        <th>Work Done / Accomplishments</th>
                        <th>Approval Status</th>
                        <th>Review Remarks</th>
                      </tr>
                    </thead>
                    <tbody>
                      {displayDailySups.map((s, idx) => (
                        <tr key={s.id || idx}>
                          <td className="font-semibold">{s.projectName || '-'}</td>
                          <td>{s.supervisorName || '-'}</td>
                          <td>
                            <div className="progress-cell-wrapper">
                              <span className="progress-pct-badge">{s.workProgress ?? 0}%</span>
                              <div className="mini-progress-bar">
                                <div className="mini-progress-fill" style={{ width: `${Math.min(100, Math.max(0, s.workProgress ?? 0))}%` }}></div>
                              </div>
                            </div>
                          </td>
                          <td style={{ maxWidth: '320px', whiteSpace: 'normal' }}>{s.description || '-'}</td>
                          <td>
                            <span className={`status-pill ${s.approvedBy && s.approvedBy !== 'Pending Approval' ? 'status-completed' : 'status-pending'}`}>
                              {s.approvedBy || 'Pending'}
                            </span>
                          </td>
                          <td>{s.reviewComment || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* TAB 2: Workforce Timesheets */}
          {activeDailyTab === 'timesheets' && (
            <div className="project-tab-content">
              <div className="tab-toolbar">
                <div className="search-box">
                  <span className="search-icon">🔍</span>
                  <input
                    type="text"
                    placeholder="Search by employee name, code, or tasks..."
                    value={dailyTimeSearch}
                    onChange={(e) => setDailyTimeSearch(e.target.value)}
                  />
                  {dailyTimeSearch && (
                    <button className="clear-btn" onClick={() => setDailyTimeSearch('')}>✕</button>
                  )}
                </div>
              </div>

              {displayDailyTimesheets.length === 0 ? (
                <div className="empty-project-detail">
                  <div className="empty-icon">⏱️</div>
                  <p>No labor timesheets logged for this date.</p>
                </div>
              ) : (
                <div className="report-table-container">
                  <table className="report-table">
                    <thead>
                      <tr>
                        <th>Emp Code</th>
                        <th>Employee Name</th>
                        <th>Assigned Project</th>
                        <th>Hours Worked</th>
                        <th>Task / Work Description</th>
                        <th>Status</th>
                        <th>Approved By</th>
                      </tr>
                    </thead>
                    <tbody>
                      {displayDailyTimesheets.map((t, idx) => (
                        <tr key={t.id || idx}>
                          <td className="font-mono">{t.employeeCode || '-'}</td>
                          <td className="font-semibold">{t.employeeName || '-'}</td>
                          <td>{t.projectName || '-'}</td>
                          <td className="font-bold text-blue">{t.hoursWorked ?? 0} hrs</td>
                          <td style={{ maxWidth: '300px', whiteSpace: 'normal' }}>{t.description || '-'}</td>
                          <td>
                            <span className={`status-pill ${t.status === 'APPROVED' ? 'status-completed' : 'status-pending'}`}>
                              {t.status || 'PENDING'}
                            </span>
                          </td>
                          <td>{t.approvedBy || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* TAB 3: Financial Transactions */}
          {activeDailyTab === 'transactions' && (
            <div className="project-tab-content">
              <div className="tab-toolbar">
                <div className="tab-filters-row">
                  <div className="search-box">
                    <span className="search-icon">🔍</span>
                    <input
                      type="text"
                      placeholder="Search voucher, payee, account head, project..."
                      value={dailyTxnSearch}
                      onChange={(e) => setDailyTxnSearch(e.target.value)}
                    />
                    {dailyTxnSearch && (
                      <button className="clear-btn" onClick={() => setDailyTxnSearch('')}>✕</button>
                    )}
                  </div>
                  <div className="pill-filters">
                    {['ALL', 'INCOME', 'EXPENSE', 'PURCHASE', 'PAYMENT'].map(type => (
                      <button
                        key={type}
                        type="button"
                        className={`pill-btn ${dailyTxnType === type ? 'active' : ''}`}
                        onClick={() => setDailyTxnType(type)}
                      >
                        {type}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {displayDailyTransactions.length === 0 ? (
                <div className="empty-project-detail">
                  <div className="empty-icon">💳</div>
                  <p>No financial transactions match the selected criteria for this date.</p>
                </div>
              ) : (
                <div className="report-table-container">
                  <table className="report-table">
                    <thead>
                      <tr>
                        <th>Voucher #</th>
                        <th>Type</th>
                        <th>Party / Payee</th>
                        <th>Project</th>
                        <th>Account Head</th>
                        <th>Payment Mode</th>
                        <th>Amount</th>
                        <th>Status</th>
                        <th>Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {displayDailyTransactions.map((x, idx) => {
                        const isIncome = String(x.transactionType).toUpperCase() === 'INCOME';
                        return (
                          <tr key={x.id || idx}>
                            <td className="font-mono">{x.transactionNumber || '-'}</td>
                            <td>
                              <span className={`txn-type-badge ${isIncome ? 'income' : 'expense'}`}>
                                {x.transactionType || 'EXPENSE'}
                              </span>
                            </td>
                            <td className="font-semibold">{x.partyName || '-'}</td>
                            <td>{x.projectName || '-'}</td>
                            <td>{x.accountHead || '-'}</td>
                            <td>{x.paymentMode || '-'}</td>
                            <td className={`font-bold ${isIncome ? 'text-green' : 'text-red'}`}>
                              {formatCurrency(x.amount)}
                            </td>
                            <td>
                              <span className="status-pill status-completed">
                                {x.status || 'APPROVED'}
                              </span>
                            </td>
                            <td>{x.description || '-'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* TAB 4: Attendance Logs */}
          {activeDailyTab === 'attendance' && (
            <div className="project-tab-content">
              <div className="tab-toolbar">
                <div className="tab-filters-row">
                  <div className="search-box">
                    <span className="search-icon">🔍</span>
                    <input
                      type="text"
                      placeholder="Search attendance by employee name, code, department..."
                      value={dailyAttSearch}
                      onChange={(e) => setDailyAttSearch(e.target.value)}
                    />
                    {dailyAttSearch && (
                      <button className="clear-btn" onClick={() => setDailyAttSearch('')}>✕</button>
                    )}
                  </div>
                  <div className="pill-filters">
                    {['ALL', 'PRESENT', 'HALF_DAY', 'ABSENT', 'LEAVE'].map(st => (
                      <button
                        key={st}
                        type="button"
                        className={`pill-btn ${dailyAttStatus === st ? 'active' : ''}`}
                        onClick={() => setDailyAttStatus(st)}
                      >
                        {st}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {displayDailyAttendance.length === 0 ? (
                <div className="empty-project-detail">
                  <div className="empty-icon">📋</div>
                  <p>No attendance records logged for this date.</p>
                </div>
              ) : (
                <div className="report-table-container">
                  <table className="report-table">
                    <thead>
                      <tr>
                        <th>Emp Code</th>
                        <th>Employee Name</th>
                        <th>Department</th>
                        <th>Status</th>
                        <th>Check In</th>
                        <th>Check Out</th>
                      </tr>
                    </thead>
                    <tbody>
                      {displayDailyAttendance.map((a, idx) => {
                        const isPresent = a.status === 'PRESENT' || a.status === 'HALF_DAY';
                        return (
                          <tr key={a.id || idx}>
                            <td className="font-mono">{a.employeeCode || '-'}</td>
                            <td className="font-semibold">{a.employeeName || '-'}</td>
                            <td>{a.department || '-'}</td>
                            <td>
                              <span className={`status-pill ${isPresent ? 'status-completed' : 'status-blocked'}`}>
                                {a.status || 'PRESENT'}
                              </span>
                            </td>
                            <td className="font-mono">{a.checkIn || '-'}</td>
                            <td className="font-mono">{a.checkOut || '-'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* TAB 5: Site Stock Details & Daily Movements */}
          {activeDailyTab === 'sitestock' && (
            <div className="project-tab-content">
              {/* Daily Site Stock KPI Summary */}
              <div className="project-kpis" style={{ marginBottom: '15px' }}>
                <div className="project-kpi-card budget">
                  <div className="kpi-icon">📦</div>
                  <div className="kpi-info">
                    <span className="kpi-title">Tracked Materials</span>
                    <span className="kpi-value">{dailyStockStats.totalItems}</span>
                    <span className="kpi-subtext">Active project items</span>
                  </div>
                </div>
                <div className="project-kpi-card income">
                  <div className="kpi-icon">🟢</div>
                  <div className="kpi-info">
                    <span className="kpi-title">Available Site Stock</span>
                    <span className="kpi-value text-green">{dailyStockStats.totalAvailable.toLocaleString()}</span>
                    <span className="kpi-subtext">Units on site</span>
                  </div>
                </div>
                <div className="project-kpi-card balance">
                  <div className="kpi-icon">💰</div>
                  <div className="kpi-info">
                    <span className="kpi-title">Site Stock Valuation</span>
                    <span className="kpi-value text-blue">{formatCurrency(dailyStockStats.totalValuation)}</span>
                    <span className="kpi-subtext">Based on unit costs</span>
                  </div>
                </div>
                <div className="project-kpi-card income">
                  <div className="kpi-icon">📥</div>
                  <div className="kpi-info">
                    <span className="kpi-title">Today's Inward</span>
                    <span className="kpi-value text-green">{dailyStockStats.inwardQty.toLocaleString()}</span>
                    <span className="kpi-subtext">Units received today</span>
                  </div>
                </div>
                <div className="project-kpi-card expense">
                  <div className="kpi-icon">🔻</div>
                  <div className="kpi-info">
                    <span className="kpi-title">Today's Consumed</span>
                    <span className="kpi-value text-red">{dailyStockStats.consumedQty.toLocaleString()}</span>
                    <span className="kpi-subtext">Units used in work today</span>
                  </div>
                </div>
              </div>

              {/* Sub-tabs & Search */}
              <div className="project-filter-bar" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px', marginBottom: '15px' }}>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    type="button"
                    className={`filter-btn ${dailyStockSubTab === 'stocks' ? 'active' : ''}`}
                    onClick={() => setDailyStockSubTab('stocks')}
                    style={{ padding: '6px 14px', borderRadius: '6px', fontWeight: '500', cursor: 'pointer', border: dailyStockSubTab === 'stocks' ? '2px solid #2563eb' : '1px solid #cbd5e1', background: dailyStockSubTab === 'stocks' ? '#eff6ff' : '#ffffff', color: dailyStockSubTab === 'stocks' ? '#1d4ed8' : '#475569' }}
                  >
                    📦 Current Site Stock Levels ({displayDailyStocks.length})
                  </button>
                  <button
                    type="button"
                    className={`filter-btn ${dailyStockSubTab === 'movements' ? 'active' : ''}`}
                    onClick={() => setDailyStockSubTab('movements')}
                    style={{ padding: '6px 14px', borderRadius: '6px', fontWeight: '500', cursor: 'pointer', border: dailyStockSubTab === 'movements' ? '2px solid #2563eb' : '1px solid #cbd5e1', background: dailyStockSubTab === 'movements' ? '#eff6ff' : '#ffffff', color: dailyStockSubTab === 'movements' ? '#1d4ed8' : '#475569' }}
                  >
                    🚚 Today's Deliveries & Material Consumed ({displayDailyMovements.length})
                  </button>
                </div>

                <div className="search-box">
                  <span className="search-icon">🔍</span>
                  <input
                    type="text"
                    placeholder="Search material, code, project..."
                    value={dailyStockSearch}
                    onChange={(e) => setDailyStockSearch(e.target.value)}
                  />
                  {dailyStockSearch && (
                    <button className="clear-btn" onClick={() => setDailyStockSearch('')}>✕</button>
                  )}
                </div>
              </div>

              {/* View 1: Current Site Stock Levels */}
              {dailyStockSubTab === 'stocks' && (
                displayDailyStocks.length === 0 ? (
                  <div className="empty-project-detail">
                    <div className="empty-icon">📦</div>
                    <p>No site stock inventory found for the selected date / project.</p>
                  </div>
                ) : (
                  <div className="report-table-container">
                    <table className="report-table">
                      <thead>
                        <tr>
                          <th>Project</th>
                          <th>Item Code</th>
                          <th>Material Name</th>
                          <th>Unit</th>
                          <th style={{ textAlign: 'right' }}>Total Received</th>
                          <th style={{ textAlign: 'right' }}>Total Consumed</th>
                          <th style={{ textAlign: 'right' }}>Available Site Stock</th>
                          <th style={{ textAlign: 'right' }}>Unit Cost</th>
                          <th style={{ textAlign: 'right' }}>Current Valuation</th>
                          <th style={{ textAlign: 'center' }}>Stock Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {displayDailyStocks.map((s, idx) => (
                          <tr key={s.itemId || idx}>
                            <td><strong>{s.projectName || '-'}</strong></td>
                            <td className="font-mono"><strong>{s.itemCode || '-'}</strong></td>
                            <td><strong>{s.itemName || '-'}</strong></td>
                            <td>{s.unitOfMeasurement || '-'}</td>
                            <td style={{ textAlign: 'right' }}>
                              {Number(s.totalReceived || 0).toLocaleString()}
                            </td>
                            <td style={{ textAlign: 'right', color: '#dc2626', fontWeight: 'bold' }}>
                              {Number(s.consumedQuantity || 0).toLocaleString()}
                            </td>
                            <td style={{ textAlign: 'right', color: '#16a34a', fontWeight: 'bold' }}>
                              {Number(s.availableQuantity || 0).toLocaleString()}
                            </td>
                            <td style={{ textAlign: 'right' }}>
                              {formatCurrency(s.unitPrice || 0)}
                            </td>
                            <td style={{ textAlign: 'right', fontWeight: 'bold' }}>
                              {formatCurrency(s.totalValue || 0)}
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              <span className={`status-pill ${Number(s.availableQuantity || 0) > 0 ? 'status-completed' : 'status-blocked'}`}>
                                {s.statusLabel || (Number(s.availableQuantity || 0) > 0 ? 'Available' : 'Fully Consumed')}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr style={{ background: '#f8fafc', fontWeight: 'bold' }}>
                          <td colSpan={6} style={{ textAlign: 'right' }}>
                            Total Site Stock:
                          </td>
                          <td style={{ textAlign: 'right', color: '#16a34a' }}>
                            {dailyStockStats.totalAvailable.toLocaleString()} Units
                          </td>
                          <td></td>
                          <td style={{ textAlign: 'right', color: '#2563eb' }}>
                            {formatCurrency(dailyStockStats.totalValuation)}
                          </td>
                          <td></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )
              )}

              {/* View 2: Today's Material Movements & Consumptions */}
              {dailyStockSubTab === 'movements' && (
                displayDailyMovements.length === 0 ? (
                  <div className="empty-project-detail">
                    <div className="empty-icon">🚚</div>
                    <p>No material movements or site consumptions recorded for this date.</p>
                  </div>
                ) : (
                  <div className="report-table-container">
                    <table className="report-table">
                      <thead>
                        <tr>
                          <th>Project</th>
                          <th>Time</th>
                          <th>Item Code</th>
                          <th>Material Name</th>
                          <th>Activity</th>
                          <th style={{ textAlign: 'right' }}>Quantity</th>
                          <th>Unit</th>
                          <th>Source / Vendor</th>
                          <th>Destination / Work Area</th>
                          <th>Notes / Purpose</th>
                          <th>Logged By</th>
                        </tr>
                      </thead>
                      <tbody>
                        {displayDailyMovements.map((m, idx) => {
                          const isConsumed = m.activityType === 'CONSUMPTION';
                          const isInward = m.activityType === 'INWARD';
                          return (
                            <tr key={m.id || idx}>
                              <td><strong>{m.projectName || '-'}</strong></td>
                              <td className="font-mono" style={{ fontSize: '12px' }}>
                                {m.transactionDate ? (m.transactionDate.includes('T') ? m.transactionDate.split('T')[1].substring(0, 5) : m.transactionDate) : '-'}
                              </td>
                              <td className="font-mono"><strong>{m.itemCode || '-'}</strong></td>
                              <td><strong>{m.itemName || '-'}</strong></td>
                              <td>
                                <span className={`status-pill ${isInward ? 'status-completed' : isConsumed ? 'status-in-progress' : 'status-pending'}`}>
                                  {m.activityType || '-'}
                                </span>
                              </td>
                              <td style={{ textAlign: 'right', fontWeight: 'bold', color: isConsumed ? '#dc2626' : isInward ? '#16a34a' : '#1e293b' }}>
                                {isConsumed ? '-' : '+'}{Number(m.quantity || 0).toLocaleString()}
                              </td>
                              <td>{m.unitOfMeasurement || '-'}</td>
                              <td style={{ fontSize: '12px', color: '#64748b' }}>{m.source || '-'}</td>
                              <td style={{ fontSize: '12px', color: '#64748b' }}>{m.destination || '-'}</td>
                              <td style={{ fontSize: '12px', color: '#475569' }}>{m.notes || m.reason || '-'}</td>
                              <td>{m.movedBy || '-'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )
              )}
            </div>
          )}
        </div>
      )}

      {/* STANDARD REPORT RESULTS (for Enquiry, Expense, Payroll, Profitability) */}
      {reportData && reportType !== 'PROJECT' && reportType !== 'DAILY' && (
        <div className="report-results">
          <div className="report-metadata">
            {reportData.companyName && (
              <p><strong>Company:</strong> {reportData.companyName}</p>
            )}
            <p><strong>Report Type:</strong> {reportData.reportType}</p>
            <p><strong>Generated At:</strong> {reportData.generatedAt}</p>
            <p><strong>Generated By:</strong> {reportData.generatedBy}</p>
          </div>

          {/* KPI Summary Overview Cards at Top */}
          {reportData.summary && Object.keys(reportData.summary).length > 0 && (
            <div className="report-summary-top">
              <div className="summary-grid">
                {Object.entries(reportData.summary)
                  .filter(([key]) => !['Filter Status', 'Start Date', 'End Date', 'Category Filter', 'Project Scope'].includes(key))
                  .map(([key, value]) => (
                    <div key={key} className="summary-item">
                      <span className="summary-label">{key}:</span>
                      <span className="summary-value">{formatValue(value, key)}</span>
                    </div>
                  ))}
              </div>
            </div>
          )}
          
          <div className="report-table-container">
            <table className="report-table">
              <thead>
                <tr>
                  {reportData.columns.map((column, index) => (
                    <th key={index}>{column}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {reportData.rows.map((row, rowIndex) => (
                  <tr key={rowIndex}>
                    {reportData.columns.map((column, colIndex) => {
                      const val = row[column];
                      if (column === 'Status') {
                        const stStr = String(val || '').toUpperCase();
                        let pillClass = 'status-pending';
                        if (stStr.includes('CONVERTED') || stStr.includes('COMPLETED') || stStr.includes('ACTIVE')) {
                          pillClass = 'status-completed';
                        } else if (stStr.includes('LOST') || stStr.includes('CANCELLED') || stStr.includes('DELAYED')) {
                          pillClass = 'status-blocked';
                        }
                        return (
                          <td key={colIndex}>
                            <span className={`status-pill ${pillClass}`}>
                              {val || '-'}
                            </span>
                          </td>
                        );
                      }
                      if (column === 'Payment Status') {
                        const stStr = String(val || '').toUpperCase();
                        const pillClass = stStr === 'PAID' ? 'status-completed' : (stStr === 'PARTIAL' ? 'status-pending' : 'status-blocked');
                        return (
                          <td key={colIndex}>
                            <span className={`status-pill ${pillClass}`}>
                              {val || '-'}
                            </span>
                          </td>
                        );
                      }
                      if (column === 'Category') {
                        return (
                          <td key={colIndex}>
                            <span className="category-pill">
                              {val || '-'}
                            </span>
                          </td>
                        );
                      }
                      if (column === 'Profit/Loss') {
                        const numVal = Number(val);
                        const isPos = !isNaN(numVal) && numVal >= 0;
                        return (
                          <td key={colIndex} className={`font-bold ${isPos ? 'text-green' : 'text-red'}`}>
                            {formatValue(val, column)}
                          </td>
                        );
                      }
                      if (column === 'Profit Margin %') {
                        const numVal = Number(val);
                        const isPos = !isNaN(numVal) && numVal >= 0;
                        return (
                          <td key={colIndex}>
                            <span className={`sup-approval-badge ${isPos ? 'approved' : 'pending'}`}>
                              {formatValue(val, column)}
                            </span>
                          </td>
                        );
                      }
                      if (column === 'Amount' && reportType === 'EXPENSE') {
                        return (
                          <td key={colIndex} className="font-bold text-red">
                            {formatValue(val, column)}
                          </td>
                        );
                      }
                      return (
                        <td key={colIndex}>{formatValue(val, column)}</td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default ReportsPage;
